import React, { useState, useEffect } from 'react';
import { useFilters } from '../context/FilterContext';
import { pollJobStatus, API_BASE } from '../services/apiService';
import { Upload, CheckCircle2, AlertCircle, ArrowRight, ArrowLeft, Loader2, Shield, Users, Building, FileSpreadsheet, Table, ChevronDown, FileText, SkipForward, Lock, EyeOff, Download, Info, MessageCircle } from 'lucide-react';

// Separate component to prevent re-mount on parent state change
const PolicyFieldInput = React.memo(({ fieldKey, label, policyFields, setPolicyFields }) => {
  const val = policyFields[fieldKey];
  const displayVal = typeof val === 'object' ? (val?.formatted || val?.raw || '') : (val || '');
  const [localVal, setLocalVal] = useState(displayVal);
  const [edited, setEdited] = useState(false);

  // Sync from parent only on mount or if not edited
  useEffect(() => { if (!edited) setLocalVal(displayVal); }, [fieldKey, displayVal]); // eslint-disable-line

  const saveToParent = (v) => {
    setPolicyFields(prev => {
      const u = { ...prev };
      if (typeof u[fieldKey] === 'object' && u[fieldKey]) {
        u[fieldKey] = { ...u[fieldKey], formatted: v, raw: v };
      } else {
        u[fieldKey] = v;
      }
      return u;
    });
  };

  return (
    <div className="flex items-center border-b border-gray-50 text-[11px]">
      <div className="w-2/5 px-3 py-1 text-gray-600">{label}</div>
      <div className="w-3/5 px-1 py-0.5">
        <input
          type="text"
          value={localVal}
          onChange={e => {
            setLocalVal(e.target.value);
            setEdited(true);
            saveToParent(e.target.value);
          }}
          className="w-full px-2 py-1 text-[11px] font-medium text-gray-800 border border-gray-200 bg-white rounded hover:border-purple-300 focus:border-purple-400 focus:outline-none focus:ring-1 focus:ring-purple-200"
        />
      </div>
    </div>
  );
});

const API = `${API_BASE}/api/preprocess`;

const PreprocessPage = ({ onJobCreated }) => {
  const { loadData } = useFilters();
  const [s3FileKey, setS3FileKey] = useState('');
  const [statusMsg, setStatusMsg] = useState('');
  const [step, _setStep] = useState(0); // 0=Landing, 1=Upload, 2=Policy, 3=MIS, 4=Review Details, 5=Verify & Generate, 6=Generating

  // Wrap setStep to push browser history so back button goes to previous step
  const setStep = (newStep) => {
    _setStep(prev => {
      if (typeof newStep === 'function') newStep = newStep(prev);
      if (newStep > prev && newStep <= 5) {
        window.history.pushState({ step: newStep }, '', window.location.pathname);
      }
      return newStep;
    });
  };

  // Handle browser back button — go to previous step instead of leaving page
  useEffect(() => {
    const handlePopState = (e) => {
      _setStep(prev => {
        if (prev > 0) {
          return prev - 1;
        }
        return 0;
      });
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Step 1: Company info + file
  const [tpaOptions, setTpaOptions] = useState([]);
  const [insurerOptions, setInsurerOptions] = useState([]);
  const [tpa, setTpa] = useState('');
  const [insurer, setInsurer] = useState('');
  const [file, setFile] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState({});
  const [missingFields, setMissingFields] = useState([]);
  const [showMissingPopup, setShowMissingPopup] = useState(false);
  const [pendingGenerate, setPendingGenerate] = useState(null);

  // Sheet & header row selection
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [headerRow, setHeaderRow] = useState(0);
  const [previewCols, setPreviewCols] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [previewRows, setPreviewRows] = useState([]);
  const [fileInfoLoading, setFileInfoLoading] = useState(false);
  const [showSheetPicker, setShowSheetPicker] = useState(false);

  // Policy extraction
  const [policyFile, setPolicyFile] = useState(null);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [policyFields, setPolicyFields] = useState(null);
  const [policyFieldDefs, setPolicyFieldDefs] = useState(null);
  const [policyMode, setPolicyMode] = useState('choose'); // choose, upload, manual
  const [policyExtractId, setPolicyExtractId] = useState(null);
  const [policyError, setPolicyError] = useState('');

  // Metadata
  const [clientName, setClientName] = useState('');
  const [policyStart, setPolicyStart] = useState('');
  const [policyEnd, setPolicyEnd] = useState('');
  const [misDate, setMisDate] = useState('');

  // Helper: format ISO date to DD-MMM-YYYY for display
  const fmtDate = (iso) => {
    if (!iso) return '';
    const [y,m,d] = iso.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${parseInt(d)}-${months[parseInt(m)-1]}-${y}`;
  };
  // Helper: parse user-typed date string to ISO (DD-MMM-YYYY, DD/MM/YYYY, DD-MM-YYYY) with validation
  const parseUserDate = (v) => {
    if (!v) return null;
    let day, month, year;
    const m1 = v.match(/(\d{1,2})[-\s]*(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*[-\s]*(\d{4})/i);
    if (m1) {
      const months = {jan:1,feb:2,mar:3,apr:4,may:5,jun:6,jul:7,aug:8,sep:9,oct:10,nov:11,dec:12};
      day = parseInt(m1[1]); month = months[m1[2].toLowerCase().slice(0,3)]; year = parseInt(m1[3]);
    } else {
      const m2 = v.match(/(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
      if (m2) { day = parseInt(m2[1]); month = parseInt(m2[2]); year = parseInt(m2[3]); }
    }
    if (!day || !month || !year) return null;
    if (day < 1 || day > 31 || month < 1 || month > 12 || year < 2000 || year > 2099) return null;
    // Validate actual date (e.g. Feb 30 is invalid)
    const testDate = new Date(year, month - 1, day);
    if (testDate.getDate() !== day || testDate.getMonth() !== month - 1) return null;
    // Date should not be in the future
    if (testDate > new Date()) return null;
    return `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
  };
  // Helper: check if date is not in future
  const isValidDate = (iso) => {
    if (!iso) return false;
    // Allow partial input while user is still typing the year
    if (iso.length < 10) return true;
    const d = new Date(iso + 'T00:00:00');
    if (isNaN(d)) return false;
    const year = d.getFullYear();
    return year >= 2000 && year <= 2099;
  };
  // Helper: set policy start and auto-calc end
  const setPolicyStartWithEnd = (iso) => {
    if (iso && !isValidDate(iso)) return;
    setPolicyStart(iso);
    if (iso) { const s = new Date(iso+'T00:00:00'); const e = new Date(s); e.setFullYear(e.getFullYear()+1); e.setDate(e.getDate()-1); setPolicyEnd(`${e.getFullYear()}-${String(e.getMonth()+1).padStart(2,'0')}-${String(e.getDate()).padStart(2,'0')}`); }
  };
  const [premium, setPremium] = useState('');
  const [premiumInception, setPremiumInception] = useState('');

  // OTP step
  const [visitorName, setVisitorName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpStep, setOtpStep] = useState('form'); // form, verify, verified
  const [leadId, setLeadId] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsExpanded, setTermsExpanded] = useState(false);
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [previewIdx, setPreviewIdx] = useState(0);
  const previewSlides = [
    { img: '/previews/dashboard-portfolio.png', label: 'Portfolio Summary' },
    { img: '/previews/dashboard-ailments.png', label: 'Ailment Analysis' },
    { img: '/previews/dashboard-maternity.png', label: 'Maternity Analysis' },
    { img: '/previews/dashboard-utilization.png', label: 'SI Utilization' },
    { img: '/previews/dashboard-relations.png', label: 'Relationship Analysis' },
    { img: '/previews/dashboard-rejections.png', label: 'Rejection Analysis' },
    { img: '/previews/dashboard-employees.png', label: 'Employee Analysis' },
    { img: '/previews/dashboard-ai-insights.png', label: 'AI Observations & Recommendations' },
    { img: '/previews/dashboard-chatbot.png', label: 'PazBot - Chat with your Claims Data' },
  ];
  useEffect(() => {
    if (step !== 0) return;
    const t = setInterval(() => setPreviewIdx(i => (i + 1) % previewSlides.length), 4000);
    return () => clearInterval(t);
  }, [step, previewSlides.length]);
  const [showClaimsInfo, setShowClaimsInfo] = useState(false);
  const [misLoading, setMisLoading] = useState(false);
  const [misManualOpen, setMisManualOpen] = useState(false);
  const [misResult, setMisResult] = useState(null); // {premium, misDate, premiums}
  const [misFile, setMisFile] = useState(null); // stored for re-extract with different sheet
  const [misSheets, setMisSheets] = useState([]); // sheet names if Excel
  const [otpLoading, setOtpLoading] = useState(false);
  const [uploadData, setUploadData] = useState(null); // stores upload response to continue after OTP

  // Step 2: Column mapping result
  const [autoMapped, setAutoMapped] = useState({});
  const [unmappedStd, setUnmappedStd] = useState([]);
  const [unmappedRaw, setUnmappedRaw] = useState([]);
  const [manualMap, setManualMap] = useState({});
  const [totalRows, setTotalRows] = useState(0);

  // Step 3: Value mappings
  const [statusValues, setStatusValues] = useState([]);
  const [typeValues, setTypeValues] = useState([]);
  const [hospTypeValues, setHospTypeValues] = useState([]);
  const [settledStatuses, setSettledStatuses] = useState([]);
  const [rejectedStatuses, setRejectedStatuses] = useState([]);
  const [closedStatuses, setClosedStatuses] = useState([]);
  const [cashlessTypes, setCashlessTypes] = useState([]);
  const [reimbTypes, setReimbTypes] = useState([]);
  const [networkTypes, setNetworkTypes] = useState([]);
  const [nonNetworkTypes, setNonNetworkTypes] = useState([]);
  const [matNormalMetro, setMatNormalMetro] = useState(50000);
  const [matCsecMetro, setMatCsecMetro] = useState(50000);
  const [matNormalNon, setMatNormalNon] = useState(50000);
  const [matCsecNon, setMatCsecNon] = useState(50000);
  const [matFromPolicy, setMatFromPolicy] = useState(false);
  const [livesInception, setLivesInception] = useState('');
  const [livesCurrent, setLivesCurrent] = useState('');
  const [empInception, setEmpInception] = useState('');
  const [empCurrent, setEmpCurrent] = useState('');

  // Load TPA/Insurer options on mount
  useEffect(() => {
    fetch(`${API}/options`).then(r => r.json()).then(d => {
      setTpaOptions(d.tpaOptions || []);
      setInsurerOptions(d.insurerOptions || []);
    }).catch(() => {});
  }, []);

  // Fetch value mappings when entering Step 2, 3, or 4
  useEffect(() => {
    if ((step === 2 || step === 3 || step === 4) && s3FileKey) {
      fetchValueMappings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, s3FileKey]);

  // Auto-poll for policy extraction result
  const [extractionDone, setExtractionDone] = useState(false);
  useEffect(() => {
    if (!policyExtractId || extractionDone) return;
    if (policyFields && Object.keys(policyFields).length > 0 && !policyFields._extract_id) {
      setExtractionDone(true);
      return;
    }
    let active = true;
    const startTime = Date.now();
    const MAX_POLL_TIME = 120000; // 2 minutes
    const poll = async () => {
      if (Date.now() - startTime > MAX_POLL_TIME) {
        if (active) {
          setPolicyError('Policy extraction timed out. You can continue without it or try re-uploading.');
          setExtractionDone(true);
        }
        return;
      }
      try {
        const r = await fetch(`${API_BASE}/api/policy/extract-status/${policyExtractId}`);
        const d = await r.json();
        if (!active) return;
        if (d.status === 'done' && d.fields) {
          setPolicyFields(d.fields);
          // Auto-fill policy start from extraction only if not already set from claim dump
          if (d.fields.inception_date) {
            const raw = d.fields.inception_date.formatted || d.fields.inception_date.raw || '';
            if (raw) {
              // Build YYYY-MM-DD directly from DD-MMM-YYYY to avoid timezone issues
              const m = raw.match(/(\d{1,2})[-\s]*(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*[-\s]*(\d{4})/i);
              if (m) {
                const months = {jan:'01',feb:'02',mar:'03',apr:'04',may:'05',jun:'06',jul:'07',aug:'08',sep:'09',oct:'10',nov:'11',dec:'12'};
                const mon = months[m[2].toLowerCase().slice(0,3)];
                if (mon) setPolicyStart(`${m[3]}-${mon}-${m[1].padStart(2,'0')}`);
              }
            }
          }
          setExtractionDone(true);
        } else if (d.status === 'error') {
          setPolicyError(d.error || 'Policy extraction failed. Please try a different file or enter details manually.');
          setExtractionDone(true);
        }
      } catch {}
    };
    poll();
    const interval = setInterval(poll, 3000);
    return () => { active = false; clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyExtractId, extractionDone]);


  // Step 1: Upload file only
  const handleUpload = async () => {
    if (!file) { setError('Please select a file'); return; }
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls', 'xlsb', 'xlsm'].includes(ext)) {
      setError(`Unsupported file format (.${ext}). Please upload CSV, XLSX, XLS, XLSB, or XLSM files only.`);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large (max 10MB). Please reduce the file size and try again.');
      return;
    }
    setLoading(true); setError(''); setStatusMsg('Uploading file...');
    try {
      const signedRes = await fetch(`${API_BASE}/api/job/signed-url?filename=${encodeURIComponent(file.name)}`).then(r => r.json());
      const fileKey = signedRes.file_key;
      const uploadUrl = signedRes.upload_url;

      if (uploadUrl.startsWith('/api/')) {
        // Backend proxy upload
        const form = new FormData();
        form.append('file', file);
        await fetch(`${API_BASE}${uploadUrl}`, { method: 'POST', body: form });
      } else {
        // Direct S3 signed URL (fallback)
        await fetch(uploadUrl, { method: 'PUT', body: file });
      }
      setS3FileKey(fileKey);

      // Check if Excel file has multiple sheets
      setStatusMsg('Analyzing file...');
      const infoRes = await fetch(`${API_BASE}/api/job/file-info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_key: fileKey }),
      }).then(r => r.json());

      // Check for file read errors (password protected, corrupt, etc.)
      if (infoRes.error) {
        setError(infoRes.error);
        setFile(null);
        setLoading(false);
        return;
      }

      // Auto-populate policy start date from extracted metadata
      if (infoRes.extractedMetadata?.policyStart && !policyStart) {
        setPolicyStart(infoRes.extractedMetadata.policyStart);
      }

      // Store preview data and column mapping for display
      if (infoRes.columns?.length > 0) {
        setPreviewCols(infoRes.columns);
        setPreviewRows(infoRes.preview || []);
      }
      if (infoRes.columnMapping) setColumnMapping(infoRes.columnMapping);

      if (infoRes.success && infoRes.sheets && infoRes.sheets.length > 1) {
        // Multiple sheets — show sheet picker
        setSheets(infoRes.sheets);
        setSelectedSheet(infoRes.sheets[0]);
        setHeaderRow(infoRes.headerRow || 0);
        setPreviewCols(infoRes.columns || []);
        setPreviewRows(infoRes.preview || []);
        setShowSheetPicker(true);
        setUploadData({ data: {}, fileKey });
        setLoading(false);
        return;
      }

      // Single sheet or CSV — show preview before proceeding
      if (infoRes.success) {
        setHeaderRow(infoRes.headerRow || 0);
      }

      setUploadData({ data: {}, fileKey });
      setShowSheetPicker(true); // Reuse sheet picker state to show preview
      setLoading(false);
    } catch (e) { setError('Upload failed: ' + e.message); setLoading(false); }
  };

  // Fetch preview when sheet or header row changes
  const handleSheetChange = async (newSheet) => {
    setSelectedSheet(newSheet);
    setFileInfoLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/job/file-info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_key: s3FileKey, sheet_name: newSheet }),
      }).then(r => r.json());
      if (res.success) {
        setHeaderRow(res.headerRow || 0);
        setPreviewCols(res.columns || []);
        setPreviewRows(res.preview || []);
      }
    } catch (e) { /* ignore */ }
    setFileInfoLoading(false);
  };

  const handleHeaderRowChange = async (newRow) => {
    setHeaderRow(newRow);
    setFileInfoLoading(true);
    try {
      const body = { file_key: s3FileKey, header_row: newRow };
      if (selectedSheet) body.sheet_name = selectedSheet;
      const res = await fetch(`${API_BASE}/api/job/file-info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then(r => r.json());
      if (res.success) {
        setPreviewCols(res.columns || []);
        setPreviewRows(res.preview || []);
      }
    } catch (e) { /* ignore */ }
    setFileInfoLoading(false);
  };

  const handleSheetConfirm = () => {
    setShowSheetPicker(false);
    setStep(2);
  };

  // OTP handlers
  const handleRequestOTP = async () => {
    if (!visitorName.trim()) { setError('Your name is required'); return; }
    if (!clientName.trim()) { setError('Company name is required'); return; }
    if (!phone.trim()) { setError('Phone number is required'); return; }
    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) { setError('Enter a valid 10-digit mobile number'); return; }
    if (/^[012]/.test(phoneDigits)) { setError('Enter a valid Indian mobile number (starts with 6-9)'); return; }
    if (!email.trim()) { setError('Email is required'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError('Enter a valid email address'); return; }
    setOtpLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/api/lead/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyName: clientName, visitorName: visitorName, email, phone, premium: parseFloat(premium) || 0, livesCount: parseFloat(livesCurrent) || 0, employeeCount: parseFloat(empCurrent) || 0 }),
      }).then(r => r.json());
      if (!res.success) setError(res.error);
      else setOtpStep('verify');
    } catch (e) { setError('Failed to send OTP'); }
    setOtpLoading(false);
  };

  const handleVerifyOTP = async () => {
    if (!otp.trim() || otp.length < 6) { setError('Enter 6-digit OTP'); return; }
    setOtpLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/api/lead/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp, leadData: { companyName: clientName, visitorName: visitorName, email, phone, premium: parseFloat(premium) || 0, livesCount: parseFloat(livesCurrent) || 0, employeeCount: parseFloat(empCurrent) || 0, livesInception: parseFloat(livesInception) || 0, employeeInception: parseFloat(empInception) || 0, premiumInception: parseFloat(premiumInception) || 0, policyStart: policyStart || '', fileKey: s3FileKey || '' } }),
      }).then(r => r.json());
      if (!res.success) { setError(res.error); setOtpLoading(false); return; }
      const newLeadId = res.lead_id || '';
      if (newLeadId) setLeadId(newLeadId);
      // OTP verified — auto-generate dashboard (pass lead_id directly, don't rely on state)
      setOtpStep('verified');
      handleMetadataSubmit(newLeadId);
    } catch (e) { setError('Verification failed'); }
    setOtpLoading(false);
  };

  // Step 3: Policy extraction handlers — upload via signed URL then start extraction
  const handlePolicyUpload = async () => {
    if (!policyFile) return;
    if (policyFile.size > 10 * 1024 * 1024) {
      setPolicyError('File too large (max 10MB). Please reduce the file size and try again.');
      return;
    }
    setPolicyLoading(true); setPolicyError('');
    setPolicyFields(null); // Reset so skeleton loader shows during extraction
    try {
      // Step 1: Get signed URLs (original + _raw.pdf)
      const signedRes = await fetch(`${API_BASE}/api/policy/signed-url?filename=${encodeURIComponent(policyFile.name)}`).then(r => r.json());
      const { upload_url, extract_id } = signedRes;

      // Step 2: Upload via backend proxy
      if (upload_url.startsWith('/api/')) {
        const form = new FormData();
        form.append('file', policyFile);
        await fetch(`${API_BASE}${upload_url}`, { method: 'POST', body: form });
      } else {
        // Direct S3 signed URL (fallback)
        const raw_upload_url = signedRes.raw_upload_url;
        await Promise.all([
          fetch(upload_url, { method: 'PUT', body: policyFile, headers: { 'Content-Type': 'application/pdf' } }),
          fetch(raw_upload_url, { method: 'PUT', body: policyFile, headers: { 'Content-Type': 'application/pdf' } }),
        ]);
      }

      // Step 3: Start extraction
      const res = await fetch(`${API_BASE}/api/policy/start-extraction`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ extract_id }),
      }).then(r => r.json());

      if (res.error) { setPolicyError(res.error); setPolicyLoading(false); return; }
      if (res.extract_id) setPolicyExtractId(res.extract_id);
      setPolicyLoading(false);
      return;
    } catch (e) { setPolicyError('Policy extraction failed: ' + e.message); }
    setPolicyLoading(false);
  };

  // MIS upload via signed URL — returns extraction result
  const uploadMisViaSignedUrl = async (file, sheetName) => {
    if (file.size > 10 * 1024 * 1024) {
      return { error: 'File too large (max 10MB). Please reduce the file size and try again.' };
    }
    // Step 1: Get signed URL
    const signedRes = await fetch(`${API_BASE}/api/mis/signed-url?filename=${encodeURIComponent(file.name)}`).then(r => r.json());
    const { upload_url, file_key } = signedRes;

    // Step 2: Upload to S3
    if (upload_url.startsWith('/api/')) {
      const form = new FormData();
      form.append('file', file);
      await fetch(`${API_BASE}${upload_url}`, { method: 'POST', body: form });
    } else {
      const contentType = file.name.endsWith('.pdf') ? 'application/pdf' : 'application/octet-stream';
      await fetch(upload_url, { method: 'PUT', body: file, headers: { 'Content-Type': contentType } });
    }

    // Step 3: Extract from S3
    const res = await fetch(`${API_BASE}/api/extract-summary-s3`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ file_key, filename: file.name, sheet_name: sheetName || null }),
    }).then(r => r.json());
    return res;
  };

  // Load policy field definitions
  const loadPolicyFieldDefs = () => {
    if (!policyFieldDefs) {
      fetch(`${API_BASE}/api/policy/fields`).then(r => r.json()).then(d => setPolicyFieldDefs(d)).catch(() => {});
    }
  };

  // The standard answers (dropdown suggestions) are wanted whenever the policy fields show.
  useEffect(() => { if (step === 2) loadPolicyFieldDefs(); }, [step]); // eslint-disable-line

  // Auto-fill inception fields from policy extraction data
  const syncPolicyFieldsToState = () => {
    if (policyFields && Object.keys(policyFields).length > 0) {
      const f = policyFields;
      const getVal = (key) => {
        const v = f[key];
        return v ? (v.formatted || v.raw || '') : '';
      };
      const parseNum = (str) => {
        if (!str) return 0;
        const s = String(str).toLowerCase();
        // Handle "35 lac", "1.5 crore", "2.5 lakh" etc.
        const lakhMatch = s.match(/([\d.]+)\s*(?:lac|lakh|l)\b/);
        if (lakhMatch) return Math.round(parseFloat(lakhMatch[1]) * 100000) || 0;
        const crMatch = s.match(/([\d.]+)\s*(?:cr|crore)\b/);
        if (crMatch) return Math.round(parseFloat(crMatch[1]) * 10000000) || 0;
        // Standard numeric with commas/₹ — strip non-numeric except dot
        const cleaned = s.replace(/[^\d.]/g, '');
        return Math.round(parseFloat(cleaned)) || 0;
      };

      // Auto-fill maternity limits — prefer metro/non-metro specific, fallback to general
      const matNormMetro = parseNum(getVal('maternity_normal_metro')) || parseNum(getVal('maternity_normal'));
      const matNormNon = parseNum(getVal('maternity_normal_nonmetro')) || parseNum(getVal('maternity_normal'));
      const matCsecMetro = parseNum(getVal('maternity_csec_metro')) || parseNum(getVal('maternity_csec'));
      const matCsecNon = parseNum(getVal('maternity_csec_nonmetro')) || parseNum(getVal('maternity_csec'));
      if (matNormMetro > 0) setMatNormalMetro(matNormMetro);
      if (matNormNon > 0) setMatNormalNon(matNormNon);
      if (matCsecMetro > 0) setMatCsecMetro(matCsecMetro);
      if (matCsecNon > 0) setMatCsecNon(matCsecNon);
      if (matNormMetro > 0 || matCsecMetro > 0 || matNormNon > 0 || matCsecNon > 0) setMatFromPolicy(true);

      // Auto-fill inception data from policy extraction
      const prem = parseNum(getVal('total_premium'));
      if (prem > 0 && !premiumInception) setPremiumInception(String(prem));
      const lives = parseNum(getVal('total_lives'));
      if (lives > 0 && !livesInception) setLivesInception(String(lives));
      const emps = parseNum(getVal('employee_count'));
      if (emps > 0 && !empInception) setEmpInception(String(emps));

      // Auto-fill premium (As On) from MIS extraction if not already set
      if (!premium && misResult?.premium) setPremium(String(Math.round(misResult.premium)));
    }
  };

  const handlePolicyContinue = () => {
    syncPolicyFieldsToState();
    setStep(4); setError('');
  };

  // Generate dashboard (called after OTP verified in step 5)
  const handleMetadataSubmit = async (directLeadId) => {
    setError('');
    setStep(6); // Generating
    if (uploadData) {
      await autoGenerate(uploadData.data, uploadData.fileKey, directLeadId);
    }
  };

  const handlePopupContinue = () => {
    if (!policyStart) { setError('Policy Start Date is required'); return; }
    setShowMissingPopup(false);
    setError('');
    setStep(6);
  };

  const autoGenerate = async (data, fileKey, directLeadId) => {
    try {
      setStatusMsg('Processing...');

      // Build inputs
      let ps = policyStart || data?.extractedMetadata?.policyStart || '';
      let pe = policyEnd || '';
      if (ps && !pe) {
        const d = new Date(ps);
        d.setFullYear(d.getFullYear() + 1);
        d.setDate(d.getDate() - 1);
        pe = d.toISOString().split('T')[0];
      }

      const manualInputs = {
        tpa: tpa || '',
        insurer: insurer || '',
        clientName: clientName || '',
        policyStart: ps,
        policyEnd: pe,
        misDate: misDate || new Date().toISOString().split('T')[0],
        premium: parseFloat(premium) || 0,
        premium_inception: parseFloat(premiumInception) || 0,
        mat_normal_metro: matNormalMetro,
        mat_csection_metro: matCsecMetro,
        mat_normal_nonmetro: matNormalNon,
        mat_csection_nonmetro: matCsecNon,
        lives_inception: parseFloat(livesInception) || 0,
        lives_current: parseFloat(livesCurrent) || 0,
        emp_inception: parseFloat(empInception) || 0,
        emp_current: parseFloat(empCurrent) || 0,
      };

      // Single combined call: detect + map values + start job
      const quickStartBody = { file_key: fileKey, manual_inputs: manualInputs, creator_email: email || '', lead_id: directLeadId || leadId || '', mis_file_key: misResult?.misFileKey || '', policy_extract_id_ref: policyExtractId || '' };
      if (selectedSheet) quickStartBody.sheet_name = selectedSheet;
      if (headerRow > 0) quickStartBody.header_row = headerRow;
      if (policyFields && Object.keys(policyFields).length > 0) quickStartBody.policy_benefits = policyFields;
      if (policyExtractId) quickStartBody.policy_extract_id = policyExtractId;
      const jobRes = await fetch(`${API_BASE}/api/job/quick-start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(quickStartBody),
      }).then(r => r.json());

      if (jobRes.error) { setError(jobRes.error); setLoading(false); return; }

      // Update state from auto-detected values
      if (jobRes.extractedMetadata?.policyStart && !policyStart) setPolicyStart(jobRes.extractedMetadata.policyStart);
      if (jobRes.extractedMetadata?.clientName && !clientName) setClientName(jobRes.extractedMetadata.clientName);
      if (jobRes.detectedAlias?.tpaName && !tpa) setTpa(jobRes.detectedAlias.tpaName);
      if (jobRes.detectedAlias?.insurerName && !insurer) setInsurer(jobRes.detectedAlias.insurerName);
      setTotalRows(jobRes.totalRows || 0);

      // Check if policy start date is missing
      if (!ps && !jobRes.policyStart) {
        setUploadData({ data: jobRes, fileKey });
        setShowMissingPopup(true);
        setLoading(false);
        return;
      }

      setStatusMsg('Generating dashboard...');
      const result = await pollJobStatus(jobRes.job_id, (stage, msg) => setStatusMsg(msg));
      if (result.error) { setError(result.error); setLoading(false); return; }
      if (onJobCreated) onJobCreated(jobRes.job_id);
      loadData(result.clientInfo, result.totalRows);
    } catch (e) { setError('Processing failed: ' + e.message); setLoading(false); }
  };

  // Required columns for report generation
  const REQUIRED_COLUMNS = [
    'Claim Number', 'Claim Status', 'Claimed Amount', 'Settled Amount', 'Claim Type',
  ];

  // Get all mapped standard columns (auto + manual)
  const getAllMappedStd = () => {
    const mapped = new Set(Object.values(autoMapped));
    Object.entries(manualMap).forEach(([std, raw]) => { if (raw) mapped.add(std); });
    return mapped;
  };

  // Fetch value mappings when entering Step 2
  const fetchValueMappings = async () => {
    try {
      const overrides = {};
      Object.entries(manualMap).forEach(([std, raw]) => { if (raw) overrides[raw] = std; });
      const body = { file_key: s3FileKey, column_overrides: overrides, column: '_all' };
      const res = await fetch(`${API}/unique-values`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      }).then(r => r.json());

      setStatusValues(res.statusValues || []);
      setTypeValues(res.typeValues || []);
      setHospTypeValues(res.hospTypeValues || []);

      const sug = res.suggestions || {};
      if (sug.cashless_types?.length) setCashlessTypes(sug.cashless_types);
      if (sug.reimbursement_types?.length) setReimbTypes(sug.reimbursement_types);
      if (sug.settled_statuses?.length) setSettledStatuses(sug.settled_statuses);
      if (sug.rejected_statuses?.length) setRejectedStatuses(sug.rejected_statuses);
      if (sug.closed_statuses?.length) setClosedStatuses(sug.closed_statuses);
      if (sug.network_types?.length) setNetworkTypes(sug.network_types);
      if (sug.non_network_types?.length) setNonNetworkTypes(sug.non_network_types);

      // Auto-populate dates from extracted metadata
      const meta = res.extractedMetadata || {};
      if (meta.policyStart && !policyStart) setPolicyStart(meta.policyStart);
    } catch (e) { /* silently fail, user can still proceed */ }
  };

  // Step 2 → Transform & Load
  const handleTransform = async () => {
    // Validate required columns
    const mappedStd = getAllMappedStd();
    const missing = REQUIRED_COLUMNS.filter(c => !mappedStd.has(c));
    if (missing.length > 0) {
      setError(`Required columns not mapped: ${missing.join(', ')}. Please map these to proceed.`);
      return;
    }
    setLoading(true); setError('');
    const overrides = {};
    Object.entries(manualMap).forEach(([std, raw]) => { if (raw) overrides[raw] = std; });

    const manualInputs = {
      tpa, insurer, clientName, policyStart, policyEnd, misDate,
      premium: parseFloat(premium) || 0,
      premium_inception: parseFloat(premiumInception) || 0,
      column_overrides: overrides,
      settled_statuses: settledStatuses,
      rejected_statuses: rejectedStatuses,
      closed_statuses: closedStatuses,
      cashless_types: cashlessTypes,
      reimbursement_types: reimbTypes,
      network_types: networkTypes,
      non_network_types: nonNetworkTypes,
      mat_normal_metro: matNormalMetro,
      mat_csection_metro: matCsecMetro,
      mat_normal_nonmetro: matNormalNon,
      mat_csection_nonmetro: matCsecNon,
      lives_inception: parseFloat(livesInception) || 0,
      lives_current: parseFloat(livesCurrent) || 0,
      emp_inception: parseFloat(empInception) || 0,
      emp_current: parseFloat(empCurrent) || 0,
    };

    try {
      // Start job
      setStatusMsg('Starting processing job...');
      const jobRes = await fetch(`${API_BASE}/api/job/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_key: s3FileKey, manual_inputs: manualInputs, creator_email: email || '', lead_id: leadId || '', mis_file_key: misResult?.misFileKey || '', policy_extract_id_ref: policyExtractId || '' }),
      });
      const { job_id } = await jobRes.json();

      // Poll until complete
      const result = await pollJobStatus(job_id, (step, msg) => setStatusMsg(msg));
      if (onJobCreated) onJobCreated(job_id);
      loadData(result.clientInfo, result.totalRows);
    } catch (e) { setError('Processing failed: ' + e.message); }
    setLoading(false);
  };

  // Multi-select helper
  const MultiSelect = ({ label, options, selected, setSelected }) => (
    <div className="mb-4">
      <label className="block text-xs font-semibold text-gray-700 mb-1.5">{label}</label>
      <div className="flex flex-wrap gap-1.5 p-2 border border-gray-200 rounded-lg bg-white min-h-[40px] max-h-[120px] overflow-y-auto">
        {options.map(opt => {
          const isSelected = selected.includes(opt);
          return (
            <button key={opt} type="button"
              onClick={() => setSelected(prev => isSelected ? prev.filter(v => v !== opt) : [...prev, opt])}
              className={`px-2 py-1 text-[11px] rounded-md transition-colors ${isSelected ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >{opt}</button>
          );
        })}
        {options.length === 0 && <span className="text-xs text-gray-400">No values found</span>}
      </div>
    </div>
  );

  const inputCls = "w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-300 focus:border-purple-400";
  const selectCls = "w-full text-xs border border-gray-200 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-purple-300";

  // Step 0 uses landing page layout, Step 1 is dedicated upload page, others use centered layout
  if (step === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-violet-50 flex flex-col">
        <div className="bg-white border-b border-gray-100 px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <a href="https://www.pazcare.com" target="_blank" rel="noopener noreferrer"><img src="/pazcare-logo.svg" alt="PazCare" className="h-6" /></a>
            <a href="/dashboard/f5592510" className="text-xs text-purple-600 hover:text-purple-800 font-medium">View Sample Dashboard →</a>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

            {/* Left - Value proposition */}
            <div>
              <h1 className="text-3xl font-bold text-gray-900 leading-tight">
                Negotiate Renewals<br />
                <span className="text-purple-600">With Data, Not Hope</span>
              </h1>
              <p className="text-gray-600 mt-4 text-sm leading-relaxed">
                See where every premium rupee goes. Upload your Claims data and get actionable insights in minutes - not weeks.
              </p>

              <div className="mt-8 space-y-5">
                {[
                  { icon: Shield, title: "Are you overpaying?", text: "See your ICR against benchmarks and walk into renewals with leverage" },
                  { icon: Users, title: "Who's driving your costs?", text: "Spot the employees, ailment, relationship who are driving most of your premium amount." },
                  { icon: Building, title: "Why are claims getting rejected?", text: "Understand rejection patterns and fix policy gaps before the next cycle" },
                  { icon: MessageCircle, title: "Chat with your claims data", text: "Ask anything about your claims data to AI assistant - AI answers with numbers, summaries, and insights" },
                  { icon: FileSpreadsheet, title: "Ready for your insurer meeting?", text: "Download an AI-generated report highlighting insights your insurers can't ignore." },
                ].map(({ icon: Icon, title, text }, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon size={16} className="text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{text}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex items-center gap-5">
                <div className="flex items-center gap-2 text-[11px] text-gray-500">
                  <div className="w-7 h-7 bg-green-50 rounded-full flex items-center justify-center"><Shield size={13} className="text-green-600" /></div>
                  <span>Data <strong>encrypted</strong></span>
                </div>
                <div className="flex items-center gap-2 text-[11px] text-gray-500">
                  <div className="w-7 h-7 bg-blue-50 rounded-full flex items-center justify-center"><Users size={13} className="text-blue-600" /></div>
                  <span>Names <strong>auto-masked</strong></span>
                </div>
              </div>
            </div>

            {/* Right - Get Started card */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
              <h2 className="text-lg font-bold text-gray-900 mb-2">Get Started</h2>
              <p className="text-xs text-gray-500 mb-6">Upload your TPA / Insurer claims data and get an AI-powered dashboard with actionable insights - takes under 2 minutes.</p>

              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3 p-3 bg-purple-50/50 rounded-lg">
                  <div className="w-7 h-7 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Upload size={14} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-800">Upload your claims dump</p>
                    <p className="text-[10px] text-gray-500">CSV or Excel file from your TPA or Insurer</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-purple-50/50 rounded-lg">
                  <div className="w-7 h-7 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <FileText size={14} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-800">Add policy & MIS details</p>
                    <p className="text-[10px] text-gray-500">Optional - for benchmarking and ICR analysis</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-purple-50/50 rounded-lg">
                  <div className="w-7 h-7 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle2 size={14} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-800">Get your dashboard & PPT</p>
                    <p className="text-[10px] text-gray-500">Interactive analytics + AI-generated presentation</p>
                  </div>
                </div>
              </div>

              <button onClick={() => setStep(1)} className="w-full py-3 text-sm font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2 shadow-sm shadow-purple-200">
                <ArrowRight size={14} /> Get Started
              </button>

              <p className="text-xs text-purple-700 font-medium text-center mt-4">With us, your <span className="font-bold text-pink-500">data</span> is safe, stays <span className="font-bold text-pink-500">anonymous</span>,<br/>and personal details like names are <span className="font-bold text-pink-500">masked</span> in reports.</p>
            </div>

          </div>
        </div>

        {/* Trusted by - client logos */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-700 font-semibold mb-3 uppercase tracking-wider">Trusted by 2200+ companies across India</p>
        </div>
        <div className="bg-gray-900 py-5 overflow-hidden">
          <div className="relative overflow-hidden">
            <div className="animate-marquee" style={{ display: 'flex', gap: '70px', alignItems: 'center', width: 'max-content' }}>
              {[
                { name: 'Open', url: '/logos/open.png' },
                { name: 'Mamaearth', url: '/logos/mamaearth.svg', h: '38px' },
                { name: 'Mindtickle', url: '/logos/mindtickle.svg', h: '38px' },
                { name: 'mCaffeine', url: '/logos/macaffine.png' },
                { name: 'Cashfree', url: '/logos/cashfree.png' },
                { name: 'Emergent', url: '/logos/emergent.svg' },
                { name: 'Greythr', url: '/logos/greythr.png' },
                { name: 'Wint', url: '/logos/wint.png' },
                { name: 'Swageasy', url: '/logos/swageasy.png' },
                { name: 'Navi', url: '/logos/navi.png' },
                { name: 'Tredence', url: '/logos/tredence.png' },
                { name: 'Raam Group', url: '/logos/raamgroup.png' },
              ].concat([
                { name: 'Open', url: '/logos/open.png' },
                { name: 'Mamaearth', url: '/logos/mamaearth.svg', h: '38px' },
                { name: 'Mindtickle', url: '/logos/mindtickle.svg', h: '38px' },
                { name: 'mCaffeine', url: '/logos/macaffine.png' },
                { name: 'Cashfree', url: '/logos/cashfree.png' },
                { name: 'Emergent', url: '/logos/emergent.svg' },
                { name: 'Greythr', url: '/logos/greythr.png' },
                { name: 'Wint', url: '/logos/wint.png' },
                { name: 'Swageasy', url: '/logos/swageasy.png' },
                { name: 'Navi', url: '/logos/navi.png' },
                { name: 'Tredence', url: '/logos/tredence.png' },
                { name: 'Raam Group', url: '/logos/raamgroup.png' },
              ]).map((logo, i) => (
                <img key={i} src={logo.url} alt={logo.name} style={{ height: logo.h || '28px', maxWidth: '120px', objectFit: 'contain', flexShrink: 0, opacity: 0.9, filter: 'brightness(0) invert(1)' }} />
              ))}
            </div>
          </div>
        </div>

        {/* Dashboard Preview Carousel - below logos */}
        <div className="px-10 py-12">
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">What you'll get</h2>
            <p className="text-sm text-gray-500 mt-1">Interactive claims analytics dashboard with 13+ sections, AI Chatbot, and PPT Report</p>
          </div>
          <div className="relative max-w-[1200px] mx-auto">
            <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-2xl">
              <div className="bg-gray-900 px-4 py-2.5 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                </div>
                <span className="text-[11px] text-gray-400 ml-2 font-medium">{previewSlides[previewIdx].label}</span>
                <a href="/dashboard/f5592510" className="ml-auto text-[11px] text-white hover:text-purple-200 font-medium">Explore live demo →</a>
              </div>
              <div className="relative bg-gray-50" style={{height: '520px'}}>
                {previewSlides.map((slide, i) => (
                  <img
                    key={i}
                    src={slide.img}
                    alt={slide.label}
                    className={`w-full h-full object-contain object-top absolute top-0 left-0 transition-opacity duration-700 ${i === previewIdx ? 'opacity-100' : 'opacity-0'}`}
                  />
                ))}
              </div>
            </div>
            {/* Navigation arrows */}
            <button onClick={() => setPreviewIdx(i => (i - 1 + previewSlides.length) % previewSlides.length)} className="absolute left-[-20px] top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-purple-600 hover:border-purple-300 transition-colors">
              <ArrowLeft size={18} />
            </button>
            <button onClick={() => setPreviewIdx(i => (i + 1) % previewSlides.length)} className="absolute right-[-20px] top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-purple-600 hover:border-purple-300 transition-colors">
              <ArrowRight size={18} />
            </button>
          </div>
          {/* Dots */}
          <div className="flex justify-center gap-2 mt-4">
            {previewSlides.map((_, i) => (
              <button key={i} onClick={() => setPreviewIdx(i)} className={`h-2 rounded-full transition-all duration-300 ${i === previewIdx ? 'bg-purple-600 w-8' : 'bg-gray-300 w-2'}`} />
            ))}
          </div>
        </div>

        {/* ===== How It Works Popup ===== */}
        {showHowItWorks && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowHowItWorks(false)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="sticky top-0 bg-white border-b border-purple-100 px-6 py-4 rounded-t-2xl flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText size={18} className="text-purple-600" />
                  <h3 className="text-sm font-bold text-gray-900">How ClaimsIQ Works</h3>
                </div>
                <button onClick={() => setShowHowItWorks(false)} className="text-gray-400 hover:text-gray-600 text-lg font-bold">✕</button>
              </div>
              <div className="px-6 py-5">
                {/* Flow Tree */}
                <div className="flex flex-col items-center gap-0">
                  {/* Step 1 */}
                  <div className="w-full max-w-sm">
                    <div className="bg-purple-600 text-white rounded-xl p-3 text-center">
                      <Upload size={20} className="mx-auto mb-1" />
                      <p className="text-xs font-bold">Upload Claims Data</p>
                      <p className="text-[10px] text-purple-200">CSV / Excel from your TPA or Insurer</p>
                      <span className="inline-block mt-1 px-2 py-0.5 bg-white/20 rounded text-[9px]">Required</span>
                    </div>
                  </div>
                  <div className="w-0.5 h-4 bg-purple-200" />

                  {/* Step 2: Optional docs */}
                  <div className="w-full max-w-sm">
                    <div className="border-2 border-dashed border-purple-200 rounded-xl p-3 bg-purple-50/30">
                      <p className="text-[10px] font-semibold text-purple-700 text-center mb-2">Supporting Documents (Optional)</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-white rounded-lg p-2 border border-gray-200 text-center">
                          <FileText size={16} className="mx-auto mb-1 text-blue-500" />
                          <p className="text-[10px] font-semibold text-gray-800">Policy Copy</p>
                          <p className="text-[9px] text-gray-500">For benchmarking</p>
                        </div>
                        <div className="bg-white rounded-lg p-2 border border-gray-200 text-center">
                          <FileSpreadsheet size={16} className="mx-auto mb-1 text-green-500" />
                          <p className="text-[10px] font-semibold text-gray-800">MIS Summary</p>
                          <p className="text-[9px] text-gray-500">Premium & dates</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="w-0.5 h-4 bg-purple-200" />

                  {/* Step 3: Verify */}
                  <div className="w-full max-w-sm">
                    <div className="bg-white rounded-xl p-3 border-2 border-amber-200 text-center">
                      <Shield size={18} className="mx-auto mb-1 text-amber-500" />
                      <p className="text-xs font-bold text-gray-800">Verify Identity (OTP)</p>
                      <p className="text-[10px] text-gray-500">Workspace email verification</p>
                    </div>
                  </div>
                  <div className="w-0.5 h-4 bg-purple-200" />

                  {/* Step 4: Review */}
                  <div className="w-full max-w-sm">
                    <div className="bg-white rounded-xl p-3 border-2 border-blue-200 text-center">
                      <CheckCircle2 size={18} className="mx-auto mb-1 text-blue-500" />
                      <p className="text-xs font-bold text-gray-800">Review & Confirm Details</p>
                      <p className="text-[10px] text-gray-500">Client name, policy period, premium, lives</p>
                    </div>
                  </div>
                  <div className="w-0.5 h-4 bg-purple-200" />

                  {/* Step 5: Dashboard */}
                  <div className="w-full max-w-sm">
                    <div className="bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl p-3 text-center">
                      <p className="text-xs font-bold">Dashboard Generated!</p>
                      <p className="text-[10px] text-purple-100">Interactive analytics + downloadable PPT</p>
                    </div>
                  </div>
                </div>

                {/* Processing info */}
                <div className="mt-5 pt-4 border-t border-gray-100">
                  <p className="text-xs font-bold text-purple-700 mb-2">Behind the Scenes</p>
                  <div className="space-y-1.5">
                    {[
                      ['Column Mapping', 'Auto-detect 265+ TPA column variations'],
                      ['Data Cleaning', 'Standardise dates, amounts, statuses'],
                      ['ICD Classification', 'Map diagnoses to disease categories'],
                      ['Computed Metrics', 'ICR, TAT, SI utilization, rejection categories'],
                      ['10+ Analytics', 'Portfolio, ailments, hospitals, demographics, maternity, etc.'],
                      ['AI Insights', 'Contextual recommendations vs industry benchmarks'],
                    ].map(([title, desc], i) => (
                      <div key={i} className="flex items-start gap-2">
                        <span className="w-4 h-4 rounded-full bg-purple-100 flex items-center justify-center text-[8px] font-bold text-purple-600 flex-shrink-0 mt-0.5">{i + 1}</span>
                        <p className="text-[11px] text-gray-600"><strong className="text-gray-800">{title}</strong> - {desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="sticky bottom-0 bg-white border-t border-purple-100 px-6 py-3 rounded-b-2xl">
                <button onClick={() => setShowHowItWorks(false)} className="w-full py-2.5 text-xs font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700">Got it</button>
              </div>
            </div>
          </div>
        )}

      </div>
    );
  }

  // Step 1: Dedicated upload page
  if (step === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-violet-50 flex flex-col">

        <div className="max-w-7xl mx-auto px-6 py-6 flex-1 flex flex-col justify-center">
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {[
              { label: 'Upload', desc: 'Claims data' },
              { label: 'Policy', desc: 'Coverage details' },
              { label: 'MIS', desc: 'Premium & ICR' },
              { label: 'Review', desc: 'Confirm details' },
              { label: 'Verify', desc: 'OTP & generate' },
            ].map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${i === 0 ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  {i + 1}
                </div>
                <div className="whitespace-nowrap">
                  <span className={`text-xs block leading-tight ${i === 0 ? 'text-purple-700 font-semibold' : 'text-gray-400'}`}>{s.label}</span>
                  <span className="text-[9px] text-gray-300 block leading-tight">{s.desc}</span>
                </div>
                {i < 4 && <div className="w-10 h-[1.5px] bg-gray-200 flex-shrink-0" />}
              </div>
            ))}
          </div>

          <div className="flex gap-8 items-start justify-center">
          {/* Left: What you get from your data */}
          <div className="hidden lg:flex flex-col w-[420px] flex-shrink-0 pt-2">
            <h3 className="text-sm font-bold text-gray-800 mb-1">What you'll get</h3>
            <p className="text-[10px] text-gray-400 mb-4">Upload your claims data and get these insights instantly</p>
            <div className="grid grid-cols-2 gap-2.5">
              {[
                { title: 'Portfolio & ICR', desc: 'Total claims, premium, incurred amount, and claims ratio at a glance.', icon: '📊' },
                { title: 'Ailment Analysis', desc: 'Top diseases by cost and frequency with ICD classification.', icon: '🏥' },
                { title: 'Settlement & TAT', desc: 'Turnaround time, cashless vs reimbursement split, and settlement rates.', icon: '⏱' },
                { title: 'Demographics', desc: 'Age, gender, and relationship-wise claims distribution.', icon: '👥' },
                { title: 'Hospital Network', desc: 'Top hospitals, network utilization, and city-wise trends.', icon: '🗺' },
                { title: 'SI Utilization', desc: 'Sum insured usage bands and high-utilization employee flagging.', icon: '📈' },
                { title: 'Employee Analysis', desc: 'Top claimants, repeat claims, and per-employee cost breakdown.', icon: '👤' },
                { title: 'Maternity Analysis', desc: 'Normal vs C-section split, within/exceeding limits.', icon: '👶' },
                { title: 'Rejection Analysis', desc: 'Rejection rate, reasons, and categories for claims denied.', icon: '❌' },
                { title: 'AI Recommendations', desc: 'AI-generated observations and actionable insights for renewal.', icon: '✨' },
              ].map((item, i) => (
                <div key={i} className="bg-white/70 border border-gray-100 rounded-xl px-3 py-2.5 flex gap-2.5 hover:shadow-sm transition-shadow">
                  <span className="text-sm flex-shrink-0">{item.icon}</span>
                  <div>
                    <p className="text-[11px] font-semibold text-gray-700">{item.title}</p>
                    <p className="text-[10px] text-gray-400 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Upload card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 w-full max-w-xl">
            <div className="flex items-start justify-between mb-1">
              <h2 className="text-lg font-bold text-gray-900">Upload Claims Data</h2>
              <button type="button" onClick={() => setShowClaimsInfo(true)} className="text-[10px] text-purple-500 hover:text-purple-700 underline whitespace-nowrap ml-2 mt-1">What is claims data?</button>
            </div>
            <p className="text-xs text-gray-500 mb-1">Upload your TPA / Insurer claims dump</p>
            <p className="text-[10px] text-gray-400 mb-6">This is the raw claims data from your TPA or insurer - used to generate portfolio analysis, settlement trends, ailment breakdowns, and more.</p>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center gap-2">
                <AlertCircle size={14} className="text-red-500 flex-shrink-0" />
                <p className="text-xs text-red-600">{error}</p>
              </div>
            )}

            {!showSheetPicker && (
              <>
                <div
                  className="border-2 border-dashed border-gray-200 rounded-xl p-10 text-center cursor-pointer hover:border-purple-400 hover:bg-purple-50/30 transition-all mb-5"
                  onClick={() => document.getElementById('raw-file-input').click()}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-purple-400', 'bg-purple-50/30'); }}
                  onDragLeave={e => { e.currentTarget.classList.remove('border-purple-400', 'bg-purple-50/30'); }}
                  onDrop={e => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-purple-400', 'bg-purple-50/30');
                    const f = e.dataTransfer.files[0];
                    if (f) {
                      const ext = f.name.split('.').pop().toLowerCase();
                      if (!['csv', 'xlsx', 'xls', 'xlsb', 'xlsm'].includes(ext)) {
                        setError(`Unsupported file format (.${ext}). Please upload CSV, XLSX, XLS, XLSB, or XLSM files only.`);
                        return;
                      }
                      setError('');
                      setFile(f);
                      setShowSheetPicker(false);
                      setSheets([]);
                    }
                  }}
                >
                  {file ? (
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle2 size={18} className="text-pink-500" />
                      <span className="text-sm text-gray-700 font-medium">{file.name}</span>
                      <button onClick={e => { e.stopPropagation(); setFile(null); setShowSheetPicker(false); setSheets([]); const inp = document.getElementById('raw-file-input'); if (inp) inp.value = ''; }} className="text-xs text-gray-400 hover:text-red-500 ml-1">✕</button>
                    </div>
                  ) : (
                    <>
                      <Upload size={36} className="mx-auto mb-3 text-purple-400" />
                      <p className="text-sm text-gray-600 mb-1">Drag & drop your claims data here</p>
                      <p className="text-xs text-gray-400 mb-3">or click to browse</p>
                      <p className="text-[10px] text-gray-400">Supported: .csv, .xlsx, .xls, .xlsb, .xlsm (max 10MB)</p>
                    </>
                  )}
                  <input id="raw-file-input" type="file" accept=".csv,.xlsx,.xls,.xlsb,.xlsm" className="hidden" onChange={e => {
                    const f = e.target.files[0];
                    if (f) {
                      const ext = f.name.split('.').pop().toLowerCase();
                      if (!['csv', 'xlsx', 'xls', 'xlsb', 'xlsm'].includes(ext)) {
                        setError(`Unsupported file format (.${ext}). Please upload CSV, XLSX, XLS, XLSB, or XLSM files only.`);
                        e.target.value = '';
                        return;
                      }
                      setError('');
                      setFile(f);
                      setShowSheetPicker(false);
                      setSheets([]);
                    }
                  }} />
                </div>

                {/* Data Safety Consent */}
                <div className="mb-4">
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={e => setTermsAccepted(e.target.checked)}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500 accent-purple-600 cursor-pointer flex-shrink-0"
                    />
                    <span className="text-[11px] text-gray-500 group-hover:text-gray-700 leading-snug">
                      I Read the <button type="button" onClick={e => { e.preventDefault(); setShowTermsPopup(true); }} className="text-purple-600 hover:text-purple-800 underline font-medium">Data Safety Terms</button>
                    </span>
                  </label>
                </div>

                <button onClick={handleUpload} disabled={loading || !file || !termsAccepted} className="w-full py-3 text-sm font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm shadow-purple-200">
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {loading ? statusMsg || 'Uploading...' : 'Upload Claims Data'}
                </button>
                {!termsAccepted && file && !loading && (
                  <p className="text-[10px] text-amber-600 text-center mt-1.5">Please check the box above to continue</p>
                )}
                {loading && (
                  <button onClick={() => { setLoading(false); setStatusMsg(''); setError(''); setFile(null); setS3FileKey(''); }} className="w-full text-xs text-gray-400 hover:text-red-500 mt-2">
                    Cancel & upload a different file
                  </button>
                )}
              </>
            )}

            {/* After upload: success banner, preview, sheet picker, continue */}
            {showSheetPicker && (
              <>
                {/* File read success banner */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-green-600 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-green-700">File read successfully</p>
                    <p className="text-[10px] text-green-600">{previewCols.length} columns detected{Object.keys(columnMapping).length > 0 ? ` · ${Object.keys(columnMapping).length} auto-mapped` : ''}{previewRows.length > 0 ? ` · Preview of first ${previewRows.length} rows` : ''}</p>
                  </div>
                </div>

                {/* Data preview table */}
                {previewCols.length > 0 && previewRows.length > 0 && (
                  <div className="border border-gray-200 rounded-lg overflow-x-auto mb-4">
                    <table className="text-[10px]">
                      <thead className="bg-gray-50">
                        <tr>{previewCols.map((c, i) => {
                          const mapped = columnMapping[String(c)];
                          return <th key={i} className={`text-left px-2.5 py-1.5 whitespace-nowrap ${mapped ? 'bg-green-50' : ''}`}>
                            <span className="font-semibold text-gray-600">{String(c)}</span>
                            {mapped && <span className="block text-[8px] text-green-600 font-normal">→ {mapped}</span>}
                          </th>;
                        })}</tr>
                      </thead>
                      <tbody>
                        {previewRows.map((row, ri) => (
                          <tr key={ri} className="border-t border-gray-100">
                            {previewCols.map((c, ci) => <td key={ci} className="px-2.5 py-1.5 text-gray-500 whitespace-nowrap">{String(row[c] ?? '').slice(0, 30)}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Sheet picker - only for multi-sheet Excel */}
                {sheets.length > 1 && (
                  <div className="mb-4 border border-purple-200 rounded-xl p-4 bg-purple-50/30 space-y-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Table size={14} className="text-purple-600" />
                      <span className="text-xs font-semibold text-gray-800">Your file has {sheets.length} sheets - select the one with claims data</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {sheets.map(s => (
                        <button
                          key={s}
                          onClick={() => handleSheetChange(s)}
                          className={`px-3 py-1.5 text-[11px] rounded-lg border transition-all ${selectedSheet === s ? 'bg-purple-600 text-white border-purple-600 font-semibold' : 'bg-white text-gray-600 border-gray-200 hover:border-purple-300'}`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-gray-600">Data starts at row:</span>
                      <select
                        value={headerRow}
                        onChange={e => handleHeaderRowChange(Number(e.target.value))}
                        className="text-[11px] border border-gray-200 rounded-md px-2 py-1 bg-white focus:ring-1 focus:ring-purple-300"
                      >
                        {[...Array(10)].map((_, i) => (
                          <option key={i} value={i}>Row {i + 1}{i === headerRow ? ' ✓' : ''}</option>
                        ))}
                      </select>
                      {fileInfoLoading && <Loader2 size={12} className="animate-spin text-purple-500" />}
                    </div>
                  </div>
                )}

                <button onClick={handleSheetConfirm} disabled={fileInfoLoading} className="w-full py-3 text-sm font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm shadow-purple-200">
                  <ArrowRight size={14} /> {sheets.length > 1 ? `Continue with "${selectedSheet}"` : 'Continue'}
                </button>
                <button onClick={() => { setFile(null); setS3FileKey(''); setShowSheetPicker(false); setSheets([]); setPreviewCols([]); setPreviewRows([]); setError(''); const inp = document.getElementById('raw-file-input'); if (inp) inp.value = ''; }} className="w-full text-xs text-gray-400 hover:text-red-500 mt-2">
                  Upload a different file
                </button>
              </>
            )}

            <div className="mt-4">
              <button onClick={() => { setStep(0); setError(''); }} className="text-xs text-gray-400 hover:text-gray-600">&larr; Back</button>
            </div>
          </div>

          </div>{/* end flex row */}
        </div>

        {/* ===== Data Safety Popup ===== */}
        {showTermsPopup && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowTermsPopup(false)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="sticky top-0 bg-white border-b border-purple-100 px-6 py-4 rounded-t-2xl flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield size={18} className="text-purple-600" />
                  <h3 className="text-sm font-bold text-gray-900">Data Safety & Privacy</h3>
                </div>
                <button onClick={() => setShowTermsPopup(false)} className="text-gray-400 hover:text-gray-600 text-lg font-bold">✕</button>
              </div>
              <div className="px-6 py-5 space-y-4">
                <div>
                  <p className="text-xs font-bold text-purple-700 mb-2">Your Data is Secure</p>
                  <div className="space-y-2 text-xs text-gray-600">
                    <div className="flex items-start gap-2"><EyeOff size={13} className="text-pink-500 mt-0.5 flex-shrink-0" /><p><strong className="text-gray-800">Names are auto-masked</strong> - Employee & patient names are redacted (e.g. "Rahul Sharma" → "R***l S***a"). The dashboard and PPT reports only show anonymised data.</p></div>
                    <div className="flex items-start gap-2"><Shield size={13} className="text-green-600 mt-0.5 flex-shrink-0" /><p><strong className="text-gray-800">Encrypted storage</strong> - Files are encrypted in transit (HTTPS/TLS) and at rest (AWS S3 AES-256). Hosted on AWS Mumbai region.</p></div>
                  </div>
                </div>
                <hr className="border-purple-100" />
                <div>
                  <p className="text-xs font-bold text-purple-700 mb-2">What We Extract & Use</p>
                  <p className="text-xs text-gray-600 mb-2">We only read columns needed for claims analysis. Everything else is ignored.</p>
                  <div className="rounded-lg border border-gray-200 overflow-hidden">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-purple-50"><th className="text-left px-3 py-1.5 text-purple-700 font-semibold">What we use</th><th className="text-left px-3 py-1.5 text-purple-700 font-semibold">Why</th></tr></thead>
                      <tbody className="text-gray-600">
                        {[
                          ['Claim Number & Status', 'Track and categorise claims'],
                          ['Claim Type', 'Cashless vs Reimbursement analysis'],
                          ['Amounts (Claimed / Settled)', 'ICR, settlement ratios, financials'],
                          ['Dates (Intimation / Settlement)', 'TAT calculation, monthly trends'],
                          ['Hospital Name & City', 'Provider analysis, metro/non-metro'],
                          ['ICD / Diagnosis', 'Ailment-wise breakdown'],
                          ['Age, Gender, Relation', 'Demographics & relation analysis'],
                          ['Sum Insured', 'SI utilization per employee'],
                          ['Employee Name / ID', 'Employee-level analysis (names masked)'],
                        ].map(([field, usage], i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                            <td className="px-3 py-1.5 font-medium text-gray-800">{field}</td>
                            <td className="px-3 py-1.5">{usage}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <hr className="border-purple-100" />
                <div className="bg-red-50 rounded-lg px-4 py-3 border border-red-100">
                  <p className="text-xs font-bold text-red-700 mb-1">What We Do NOT Store</p>
                  <p className="text-xs text-red-600/80">Aadhaar, PAN, bank account, salary, government IDs - these are ignored even if present in your file.</p>
                </div>
                <hr className="border-purple-100" />
                <div>
                  <p className="text-xs font-bold text-purple-700 mb-2">No Data Sharing</p>
                  <ul className="text-xs text-gray-600 space-y-1 list-disc ml-4">
                    <li>Your data is used <strong>only</strong> to generate your report</li>
                    <li>Not shared with any insurer, TPA, or third party</li>
                    <li>Not used for marketing, profiling, or AI training</li>
                    <li>Dashboard access is protected with a unique URL</li>
                  </ul>
                </div>
              </div>
              <div className="sticky bottom-0 bg-white border-t border-purple-100 px-6 py-3 rounded-b-2xl">
                <button onClick={() => setShowTermsPopup(false)} className="w-full py-2.5 text-xs font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700">Got it</button>
              </div>
            </div>
          </div>
        )}

        {/* ===== What is Claims Data Popup ===== */}
        {showClaimsInfo && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowClaimsInfo(false)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="sticky top-0 bg-white border-b border-purple-100 px-6 py-4 rounded-t-2xl flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet size={18} className="text-purple-600" />
                  <h3 className="text-sm font-bold text-gray-900">What is Claims Data?</h3>
                </div>
                <button onClick={() => setShowClaimsInfo(false)} className="text-gray-400 hover:text-gray-600 text-lg font-bold">✕</button>
              </div>
              <div className="px-6 py-5 space-y-5">
                {/* What */}
                <div>
                  <p className="text-xs text-gray-600 leading-relaxed">Claims data is an <strong className="text-gray-800">Excel or CSV file</strong> that contains details of all health insurance claims filed by your employees. It includes information like claim amounts, hospitals visited, diagnoses, settlement status, and more.</p>
                </div>

                {/* Where to get */}
                <div>
                  <p className="text-xs font-bold text-purple-700 mb-2">Where to get it?</p>
                  <p className="text-xs text-gray-600 leading-relaxed">Get the <strong className="text-purple-700">Claims Dump</strong>, <strong className="text-purple-700">Policy Copy</strong>, and <strong className="text-purple-700">MIS Report</strong> from your <strong className="text-pink-600">Insurer</strong>, <strong className="text-pink-600">TPA</strong>, or <strong className="text-pink-600">Broker</strong>.</p>
                </div>

                {/* Sample Preview */}
                <div>
                  <p className="text-xs font-bold text-purple-700 mb-2">Sample: How it looks</p>
                  <div className="rounded-lg border border-gray-200 overflow-x-auto text-[8px]">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-purple-50">
                          {['Claim No', 'Emp ID', 'Member', 'Patient', 'Age', 'Gender', 'Relation', 'Sum Insured', 'Hospital', 'City', 'Network', 'ICD', 'Type', 'Status', 'Intimation', 'Settlement', 'Claimed', 'Settled'].map((h, i) => (
                            <th key={i} className="px-1 py-1.5 text-left text-purple-700 font-semibold whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="text-gray-600">
                        {[
                          ['CLM001', 'EMP001', 'R***h K***r', 'R***h K***r', '35', 'M', 'Self', '5,00,000', 'Apollo Hospital', 'Mumbai', 'Network', 'I10', 'Cashless', 'Settled', '15-05-25', '20-05-25', '45,000', '42,000'],
                          ['CLM002', 'EMP001', 'R***h K***r', 'S***a K***r', '32', 'F', 'Spouse', '3,00,000', 'Fortis Hospital', 'Mumbai', 'Network', 'O80', 'Cashless', 'Settled', '10-06-25', '16-06-25', '48,000', '45,000'],
                          ['CLM003', 'EMP002', 'P***a S***a', 'P***a S***a', '28', 'F', 'Self', '5,00,000', 'Max Hospital', 'Delhi', 'Network', 'J06', 'Reimb.', 'Settled', '20-06-25', '05-07-25', '22,000', '20,000'],
                          ['CLM004', 'EMP003', 'A***t P***l', 'A***t P***l', '42', 'M', 'Self', '5,00,000', 'Sterling Hosp.', 'Ahmedabad', 'Network', 'K35', 'Cashless', 'Settled', '01-07-25', '06-07-25', '85,000', '80,000'],
                          ['CLM005', 'EMP003', 'A***t P***l', 'K***a P***l', '68', 'F', 'Parents', '2,00,000', 'Civil Hospital', 'Ahmedabad', 'Non-Net.', 'M17', 'Reimb.', 'Settled', '15-07-25', '01-08-25', '2,50,000', '1,80,000'],
                          ['CLM006', 'EMP004', 'V***m S***h', 'V***m S***h', '45', 'M', 'Self', '5,00,000', 'Medanta Hosp.', 'Gurugram', 'Network', 'I21', 'Cashless', 'O/S', '05-08-25', '—', '3,50,000', '—'],
                          ['CLM007', 'EMP005', 'D***k J***i', 'D***k J***i', '38', 'M', 'Self', '5,00,000', 'Manipal Hosp.', 'Bangalore', 'Network', 'L00', 'Reimb.', 'Rejected', '25-08-25', '—', '1,20,000', '—'],
                        ].map((row, ri) => (
                          <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                            {row.map((cell, ci) => (
                              <td key={ci} className="px-1 py-1 whitespace-nowrap">{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[9px] text-gray-400 mt-1.5 text-center">Your file may have different column names - ClaimsIQ auto-maps 265+ column variations</p>
                </div>

                {/* Tip */}
                <div className="bg-amber-50 rounded-lg px-4 py-3 border border-amber-100">
                  <p className="text-[10px] text-amber-800"><strong>Tip:</strong> Upload the raw dump as-is from your TPA/Insurer. No need to clean or format - ClaimsIQ handles everything automatically.</p>
                </div>
              </div>
              <div className="sticky bottom-0 bg-white border-t border-purple-100 px-6 py-3 rounded-b-2xl">
                <button onClick={() => setShowClaimsInfo(false)} className="w-full py-2.5 text-xs font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700">Got it</button>
              </div>
            </div>
          </div>
        )}

      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-violet-50 flex items-center justify-center p-6">
      <div className={`w-full transition-all duration-300 ${step === 2 && (policyMode === 'manual' || policyExtractId || (policyFields && Object.keys(policyFields).length > 0)) ? 'max-w-[96%]' : 'max-w-6xl'}`}>
        {/* Progress Steps - shown for step >= 2 */}
        {step >= 2 && (
        <div className="flex items-center justify-center gap-2 mb-8">
          {[
            { label: 'Upload', desc: 'Claims data' },
            { label: 'Policy', desc: 'Coverage details' },
            { label: 'MIS', desc: 'Premium & ICR' },
            { label: 'Review', desc: 'Confirm details' },
            { label: 'Verify', desc: 'OTP & generate' },
          ].map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${step > i + 1 ? 'bg-pink-500 text-white' : step === i + 1 ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                {step > i + 1 ? '✓' : i + 1}
              </div>
              <div className="whitespace-nowrap">
                <span className={`text-xs block leading-tight ${step === i + 1 ? 'text-purple-700 font-semibold' : step > i + 1 ? 'text-pink-500' : 'text-gray-400'}`}>{s.label}</span>
                <span className={`text-[9px] block leading-tight ${step > i + 1 ? 'text-pink-300' : 'text-gray-300'}`}>{s.desc}</span>
              </div>
              {i < 4 && <div className={`w-10 h-[1.5px] flex-shrink-0 ${step > i + 1 ? 'bg-pink-200' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>
        )}

        {/* ====== STEP 3: MIS Report ====== */}
        {step === 3 && (
          <div className="flex gap-8 items-start justify-center">
          <div className="hidden lg:flex flex-col w-72 flex-shrink-0 pt-2">
            <h3 className="text-sm font-bold text-gray-800 mb-1">Why upload MIS report?</h3>
            <p className="text-[10px] text-gray-400 mb-3">MIS data powers accurate ICR and renewal estimates</p>
            <div className="grid grid-cols-1 gap-2.5">
            {[
              { title: 'ICR Calculation', desc: 'Premium is needed to calculate Incurred Claim Ratio - the #1 metric for policy health and renewal pricing.', icon: '📊' },
              { title: 'Renewal Premium', desc: 'With premium + lives data, we estimate your expected renewal range (aggressive to conservative).', icon: '💰' },
              { title: 'Accurate Projections', desc: 'MIS date sets the data cutoff - so annualized claims, trend projections, and elapsed days are precise.', icon: '📅' },
              { title: 'Lives & Employees', desc: 'We auto-extract current lives, inception lives, and employee counts for utilization analysis.', icon: '👥' },
              { title: 'No MIS? No problem', desc: 'Enter premium and date manually, or skip - dashboard works without it. ICR just won\'t be available.', icon: '✅' },
              { title: 'Where to find it?', desc: 'Ask your insurer or TPA for the latest monthly MIS report. Usually shared via email as Excel/PDF.', icon: '📧' },
            ].map((item, i) => (
              <div key={i} className="bg-white/70 border border-gray-100 rounded-xl px-3 py-2.5 flex gap-2.5">
                <span className="text-sm flex-shrink-0">{item.icon}</span>
                <div>
                  <p className="text-[11px] font-semibold text-gray-700">{item.title}</p>
                  <p className="text-[10px] text-gray-400 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 w-full max-w-lg">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 size={16} className="text-pink-500" />
              <span className="text-xs text-gray-500">Policy step complete &bull; MIS Report</span>
            </div>

            {error && <p className="text-xs text-red-500 mb-3 mt-2">{error}</p>}

            <h2 className="text-lg font-bold text-gray-900 mt-3 mb-1">MIS Report <span className="text-sm font-normal text-gray-400">(Optional)</span></h2>
            <p className="text-xs text-gray-500 mb-1">Upload your MIS to auto-fill premium & report date for precise ICR</p>
            <p className="text-[10px] text-gray-400 mb-4">The MIS report from your insurer/TPA contains premium paid and reporting period - needed to calculate Incurred Claim Ratio (ICR) accurately.</p>

            {/* Hero upload area OR success state */}
            {misLoading ? (
              <div className="flex flex-col items-center gap-3 p-6 border-2 border-blue-200 rounded-xl bg-blue-50 mb-4">
                <Loader2 size={28} className="animate-spin text-blue-500" />
                <p className="text-xs text-blue-600 font-medium">Extracting Premium & MIS Date...</p>
              </div>
            ) : misResult ? (
              <div className="border-2 border-green-200 rounded-xl bg-green-50 p-4 mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 size={16} className="text-green-600" />
                  <span className="text-xs font-semibold text-green-700">MIS data extracted successfully</span>
                  <button onClick={() => { setMisResult(null); setMisFile(null); setMisSheets([]); setPremium(''); }} className="text-[10px] text-gray-400 hover:text-red-500 ml-auto">Change file</button>
                </div>
                {misFile && <p className="text-[10px] text-green-600 mb-3 flex items-center gap-1"><FileSpreadsheet size={11} /> {misFile.name}</p>}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[9px] text-green-700 font-medium">Premium (Till Date) Exc. GST</label>
                    <input type="text" value={premium ? `₹${Number(premium).toLocaleString('en-IN')}` : ''} onChange={e => { const val = e.target.value.replace(/[^0-9.]/g, ''); setPremium(val || ''); setMisResult(prev => ({ ...prev, premium: val ? parseFloat(val) : null })); }} className="w-full px-3 py-2 text-sm border border-green-200 rounded-lg bg-white focus:border-purple-400 focus:outline-none" placeholder="e.g. ₹50,00,000" />
                  </div>
                  <div>
                    <label className="text-[9px] text-green-700 font-medium">MIS Date</label>
                    <input type="date" min="2000-01-01" value={misResult?.misDate || misDate || ''} max={new Date().toISOString().split('T')[0]} onChange={e => { setMisDate(e.target.value); setMisResult(prev => ({ ...prev, misDate: e.target.value })); }} className="w-full px-3 py-2 text-sm border border-green-200 rounded-lg bg-white focus:border-purple-400 focus:outline-none" />
                  </div>
                </div>
                {misSheets.length > 1 && misFile && (
                  <div className="mt-3 pt-2 border-t border-green-200">
                    <p className="text-[10px] text-gray-500 mb-1">Wrong values? Try a different sheet:</p>
                    <div className="flex flex-wrap gap-1">
                      {misSheets.map(s => (
                        <button key={s} onClick={async () => {
                          setMisLoading(true); setError(''); setMisResult(null);
                          try {
                            const res = await uploadMisViaSignedUrl(misFile, s);
                            if (res.error) { setError(res.error); setMisLoading(false); return; }
                            if (res.premium) setPremium(String(Math.round(res.premium)));
                            if (res.misDate) setMisDate(res.misDate);
                            if (res.livesAsOn && !livesCurrent) setLivesCurrent(String(res.livesAsOn));
                            if (res.employeesAsOn && !empCurrent) setEmpCurrent(String(res.employeesAsOn));
                            // Inception data from MIS — only if not already set from policy extraction
                            if (!premiumInception) {
                              const ip = res.inceptionPremium || res.premiums?.inception_premium;
                              if (ip) setPremiumInception(String(Math.round(ip)));
                            }
                            if (!livesInception && res.livesInception) setLivesInception(String(res.livesInception));
                            if (res.premium || res.misDate) setMisResult({ premium: res.premium, premiums: res.premiums, misDate: res.misDate, misFileKey: res.misFileKey });
                          } catch (err) { setError('Failed: ' + err.message); }
                          setMisLoading(false);
                        }} className="px-2 py-0.5 text-[10px] bg-white border border-green-300 rounded hover:bg-green-100">{s}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <label className="block mb-4 cursor-pointer"
                onDragOver={e => { e.preventDefault(); e.currentTarget.querySelector('.drop-zone')?.classList.add('border-blue-400', 'bg-blue-50'); }}
                onDragLeave={e => { e.currentTarget.querySelector('.drop-zone')?.classList.remove('border-blue-400', 'bg-blue-50'); }}
                onDrop={e => { e.preventDefault(); e.currentTarget.querySelector('.drop-zone')?.classList.remove('border-blue-400', 'bg-blue-50'); const f = e.dataTransfer.files[0]; if (f) { const ext = f.name.split('.').pop().toLowerCase(); if (!['pdf','xlsx','xls','xlsb','xlsm','csv'].includes(ext)) { setError('Unsupported format.'); return; } setError(''); setMisLoading(true); setMisFile(f); uploadMisViaSignedUrl(f).then(res => { if (res.error) { setError(res.error); setMisLoading(false); return; } if (res.sheets?.length > 1) setMisSheets(res.sheets); if (res.premium) setPremium(String(Math.round(res.premium))); if (res.misDate) setMisDate(res.misDate); if (res.policyStartDate) setPolicyStart(res.policyStartDate); if (res.premium || res.misDate) setMisResult({ premium: res.premium, premiums: res.premiums, misDate: res.misDate, misFileKey: res.misFileKey }); else setMisResult({ premium: null, premiums: {}, misDate: null, misFileKey: res.misFileKey || '' }); setMisLoading(false); }).catch(err => { setError('Failed: ' + err.message); setMisLoading(false); }); } }}
              >
                <div className="drop-zone border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-purple-400 hover:bg-purple-50/30 transition-all">
                  <Upload size={32} className="text-purple-300 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-700 mb-1">Drag & drop your MIS Report</p>
                  <p className="text-[10px] text-gray-400 mb-3">PDF, Excel (.xlsx, .xls) - from your TPA or Insurer (max 10MB)</p>
                  <span className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-purple-600 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100">
                    <FileSpreadsheet size={14} /> Browse Files
                  </span>
                </div>
                <input type="file" accept=".pdf,.xlsx,.xls,.xlsb,.xlsm,.csv" className="hidden" onChange={async e => {
                  const f = e.target.files[0]; if (!f) return;
                  setError(''); setMisLoading(true); setMisResult(null); setMisFile(f);
                  try {
                    const res = await uploadMisViaSignedUrl(f);
                    if (res.error) { setError(res.error); setMisLoading(false); return; }
                    if (res.sheets?.length > 1) setMisSheets(res.sheets);
                    if (res.premium) setPremium(String(Math.round(res.premium)));
                    if (res.misDate) setMisDate(res.misDate);
                    if (res.policyStartDate) setPolicyStart(res.policyStartDate);
                    if (res.livesAsOn && !livesCurrent) setLivesCurrent(String(res.livesAsOn));
                    if (res.employeesAsOn && !empCurrent) setEmpCurrent(String(res.employeesAsOn));
                    // Inception data from MIS — only if not already set from policy extraction
                    if (!premiumInception) {
                      const ip = res.inceptionPremium || res.premiums?.inception_premium;
                      if (ip) setPremiumInception(String(Math.round(ip)));
                    }
                    if (!livesInception && res.livesInception) setLivesInception(String(res.livesInception));
                    if (res.premium || res.misDate) setMisResult({ premium: res.premium, premiums: res.premiums, misDate: res.misDate, misFileKey: res.misFileKey });
                    else { setMisResult({ premium: null, premiums: {}, misDate: null, misFileKey: res.misFileKey || '' }); }
                  } catch (err) { setError('Failed to extract: ' + err.message); }
                  setMisLoading(false); e.target.value = '';
                }} />
              </label>
            )}

            {/* Manual entry toggle - only when no upload */}
            {!misResult && !misLoading && (
              <>
                <div className="flex items-center gap-3 mt-1 mb-3">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-[10px] text-gray-400 font-medium">OR</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
                {!misManualOpen ? (
                  <button onClick={() => setMisManualOpen(true)} className="w-full mb-4 py-2 text-xs font-semibold text-purple-600 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 flex items-center justify-center gap-2">
                    <FileText size={13} /> Enter Premium & MIS Date Manually
                  </button>
                ) : (
                  <div className="mb-4 grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[9px] text-gray-400">Premium (Till Date) Exc. GST</label>
                      <input type="text" value={premium ? `₹${Number(premium).toLocaleString('en-IN')}` : ''} onChange={e => { const val = e.target.value.replace(/[^0-9.]/g, ''); setPremium(val || ''); }} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-purple-400 focus:outline-none" placeholder="e.g. ₹50,00,000" />
                    </div>
                    <div>
                      <label className="text-[9px] text-gray-400">MIS Date</label>
                      <input type="date" min="2000-01-01" value={misDate || ''} max={new Date().toISOString().split('T')[0]} onChange={e => setMisDate(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-purple-400 focus:outline-none" />
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ICR accuracy hint */}
            <div className="flex items-start gap-2 mb-4 p-2.5 bg-amber-50 border border-amber-100 rounded-lg">
              <AlertCircle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-[10px] text-amber-700">Without premium data, ICR (Incurred Claims Ratio) will show as 0%. This is the most critical metric for renewal negotiations.</p>
            </div>

            <button onClick={() => {
              if (misDate) {
                const yr = new Date(misDate + 'T00:00:00').getFullYear();
                if (isNaN(yr) || yr < 2000 || yr > 2099) { setError('Please enter a valid MIS date (year between 2000-2099)'); return; }
              }
              setStep(4); setError('');
            }} className="w-full py-2.5 text-sm font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2">
              <ArrowRight size={14} /> Continue
            </button>
            <div className="flex justify-between mt-2">
              <button onClick={() => { setStep(2); setError(''); }} className="text-xs text-gray-400 hover:text-gray-600">&larr; Back</button>
              <button onClick={() => { setStep(4); setError(''); }} className="text-xs text-purple-500 hover:text-purple-700">Skip for now &rarr;</button>
            </div>
          </div>
          </div>/* end MIS flex */
        )}

        {/* ====== STEP 4: Review Details ====== */}
        {step === 4 && (
          <div className="flex gap-6 items-start justify-center">
          <div className="hidden lg:flex flex-col gap-4 w-60 flex-shrink-0 pt-6">
            {[
              { title: 'Maternity Limits', desc: 'Used to identify claims exceeding the assured coverage limit for normal delivery and C-section.', icon: '🤰' },
              { title: 'Auto-filled fields', desc: 'Purple-highlighted fields were extracted from your documents. Verify they are correct.', icon: '🤖' },
              { title: 'Editable', desc: 'All fields are editable - click any value to correct it before generating the report.', icon: '✏️' },
            ].map((item, i) => (
              <div key={i} className="bg-white/70 border border-gray-100 rounded-xl p-4 flex gap-3">
                <span className="text-xl flex-shrink-0">{item.icon}</span>
                <div>
                  <p className="text-sm font-semibold text-gray-700">{item.title}</p>
                  <p className="text-xs text-gray-400 leading-relaxed mt-1">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-2xl border border-gray-200 p-6 w-full max-w-xl">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 size={16} className="text-pink-500" />
              <span className="text-xs text-gray-500">MIS complete &bull; Review Details</span>
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Review Details</h2>
            <p className="text-[10px] text-gray-400 mb-2">Verify the auto-extracted details below. Accurate policy dates, premium, and lives count ensure correct ICR, utilization ratios, and benchmarking.</p>
            <div className="flex items-center gap-3 mb-4 text-[9px]">
              <span className="flex items-center gap-1 text-purple-500"><span className="w-3 h-3 rounded bg-purple-50 border border-purple-200" /> Extracted from your documents</span>
              <span className="flex items-center gap-1 text-gray-400"><span className="w-3 h-3 rounded bg-white border border-gray-200" /> Default or not provided</span>
            </div>

            {/* Section A: Policy Basics */}
            <div className="mb-4">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Policy Basics</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Policy Start Date <span className="text-red-400">*</span></label>
                  <div className="flex items-center gap-1">
                    <input type="text" key={`ps-${policyStart}`} defaultValue={fmtDate(policyStart)} onBlur={e => { const iso = parseUserDate(e.target.value); if (iso) { setPolicyStartWithEnd(iso); e.target.style.borderColor = ''; } else if (e.target.value.trim()) { e.target.value = fmtDate(policyStart); e.target.style.borderColor = '#ef4444'; setTimeout(() => { e.target.style.borderColor = ''; }, 1500); } }} className={`${inputCls} flex-1 ${policyStart ? 'bg-purple-50/30 border-purple-200' : ''}`} placeholder="e.g. 16-May-2025" />
                    <div className="relative"><input type="date" min="2000-01-01" value={policyStart} max={new Date().toISOString().split('T')[0]} onChange={e => setPolicyStartWithEnd(e.target.value)} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" /><span className="px-2 py-2 border border-gray-200 rounded-lg bg-white text-gray-400 text-xs cursor-pointer hover:bg-gray-50">📅</span></div>
                  </div>
                                  </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">MIS Date <span className="text-red-400">*</span></label>
                  <div className="flex items-center gap-1">
                    <input type="text" key={`mis-${misDate}`} defaultValue={fmtDate(misDate)} onBlur={e => { const iso = parseUserDate(e.target.value); if (iso) { setMisDate(iso); e.target.style.borderColor = ''; } else if (e.target.value.trim()) { e.target.value = fmtDate(misDate); e.target.style.borderColor = '#ef4444'; setTimeout(() => { e.target.style.borderColor = ''; }, 1500); } }} className={`${inputCls} flex-1 ${misDate ? 'bg-purple-50/30 border-purple-200' : ''}`} placeholder="e.g. 20-Apr-2026" />
                    <div className="relative"><input type="date" min="2000-01-01" value={misDate} max={new Date().toISOString().split('T')[0]} onChange={e => setMisDate(e.target.value)} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" /><span className="px-2 py-2 border border-gray-200 rounded-lg bg-white text-gray-400 text-xs cursor-pointer hover:bg-gray-50">📅</span></div>
                  </div>
                                  </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Premium (As On) <span className="text-[8px] text-gray-400">Exc. GST</span> <span className="text-red-400">*</span></label>
                  <input type="number" value={premium} onChange={e => setPremium(e.target.value)} className={`${inputCls} ${premium ? 'bg-purple-50/30 border-purple-200' : ''}`} placeholder="e.g. 7289994" />
                                  </div>
              </div>
            </div>

            <div className="border-t border-gray-100 my-3" />

            {/* Section B: Inception Details */}
            <div className="mb-4">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Inception Details</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Premium (Inception) <span className="text-[8px] text-gray-400">Exc. GST</span></label>
                  <input type="number" value={premiumInception} onChange={e => setPremiumInception(e.target.value)} className={`${inputCls} ${premiumInception ? 'bg-purple-50/30 border-purple-200' : ''}`} placeholder="Not provided - click to add" />
                                  </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Lives (Inception)</label>
                  <input type="number" value={livesInception} onChange={e => setLivesInception(e.target.value)} className={`${inputCls} ${livesInception ? 'bg-purple-50/30 border-purple-200' : ''}`} placeholder="Not provided - click to add" />
                                  </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Employees (Inception)</label>
                  <input type="number" value={empInception} onChange={e => setEmpInception(e.target.value)} className={`${inputCls} ${empInception ? 'bg-purple-50/30 border-purple-200' : ''}`} placeholder="Not provided - click to add" />
                                  </div>
              </div>
            </div>

            <div className="border-t border-gray-100 my-3" />

            {/* Section C: Demographics (Current) */}
            <div className="mb-4">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Current Demographics</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Lives (Current)</label>
                  <input type="number" value={livesCurrent} onChange={e => setLivesCurrent(e.target.value)} className={`${inputCls} ${livesCurrent ? 'bg-purple-50/30 border-purple-200' : ''}`} placeholder="Not provided - click to add" />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Employees (Current)</label>
                  <input type="number" value={empCurrent} onChange={e => setEmpCurrent(e.target.value)} className={`${inputCls} ${empCurrent ? 'bg-purple-50/30 border-purple-200' : ''}`} placeholder="Not provided - click to add" />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 my-3" />

            {/* Section C: Maternity Limits - 2x2 grid */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Maternity Limits</p>
                {matFromPolicy && <span className="inline-flex items-center gap-0.5 text-[8px] text-purple-500 bg-purple-50 px-1.5 py-0.5 rounded-full"><CheckCircle2 size={8} /> From policy extraction</span>}
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                <div className={`rounded-lg p-2 ${matFromPolicy ? 'bg-purple-50/30 border border-purple-100' : 'bg-gray-50'}`}>
                  <p className="text-[9px] text-gray-400 mb-1.5 font-medium">Metro</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className="block text-[9px] text-gray-500 mb-0.5">Normal</label><input type="number" value={matNormalMetro} onChange={e => setMatNormalMetro(Number(e.target.value))} className={inputCls + ' text-[11px]'} /></div>
                    <div><label className="block text-[9px] text-gray-500 mb-0.5">C-Section</label><input type="number" value={matCsecMetro} onChange={e => setMatCsecMetro(Number(e.target.value))} className={inputCls + ' text-[11px]'} /></div>
                  </div>
                </div>
                <div className={`rounded-lg p-2 ${matFromPolicy ? 'bg-purple-50/30 border border-purple-100' : 'bg-gray-50'}`}>
                  <p className="text-[9px] text-gray-400 mb-1.5 font-medium">Non-Metro</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className="block text-[9px] text-gray-500 mb-0.5">Normal</label><input type="number" value={matNormalNon} onChange={e => setMatNormalNon(Number(e.target.value))} className={inputCls + ' text-[11px]'} /></div>
                    <div><label className="block text-[9px] text-gray-500 mb-0.5">C-Section</label><input type="number" value={matCsecNon} onChange={e => setMatCsecNon(Number(e.target.value))} className={inputCls + ' text-[11px]'} /></div>
                  </div>
                </div>
              </div>
            </div>


            {error && <p className="text-xs text-red-500 mb-3">{error}</p>}

            <button onClick={() => {
              if (!policyStart) { setError('Policy Start Date is required'); return; }
              if (policyStart) { const yr = new Date(policyStart + 'T00:00:00').getFullYear(); if (isNaN(yr) || yr < 2000 || yr > 2099) { setError('Please enter a valid Policy Start Date'); return; } }
              if (!premium) { setError('Premium (As On) is required'); return; }
              if (misDate) { const yr = new Date(misDate + 'T00:00:00').getFullYear(); if (isNaN(yr) || yr < 2000 || yr > 2099) { setError('Please enter a valid MIS Date (year between 2000-2099)'); return; } }
              if (!misDate) setMisDate(new Date().toISOString().split('T')[0]);
              setStep(5); setError('');
            }} className="w-full py-2.5 text-sm font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2">
              <ArrowRight size={14} /> Continue
            </button>
            <div className="flex justify-between mt-2">
              <button onClick={() => { setStep(3); setError(''); }} className="text-xs text-gray-400 hover:text-gray-600">&larr; Back</button>
            </div>
          </div>
          <div className="hidden lg:flex flex-col gap-4 w-60 flex-shrink-0 pt-6">
            {[
              { title: 'Accurate data = better insights', desc: 'Correct premium, lives, and policy dates help estimate renewal premium, per-capita claim cost, and utilization ratios - key inputs for renewal negotiations.', icon: '📊' },
              { title: 'Policy Period', desc: 'Start and end dates define the claim window for trend analysis and annualized projections.', icon: '📅' },
              { title: 'Premium', desc: 'Premium excluding GST is used to calculate ICR - the ratio of claims to premium paid.', icon: '💰' },
            ].map((item, i) => (
              <div key={i} className="bg-white/70 border border-gray-100 rounded-xl p-4 flex gap-3">
                <span className="text-xl flex-shrink-0">{item.icon}</span>
                <div>
                  <p className="text-sm font-semibold text-gray-700">{item.title}</p>
                  <p className="text-xs text-gray-400 leading-relaxed mt-1">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          </div>/* end Review flex */
        )}

        {/* ====== STEP 2: Policy Benefits (optional) ====== */}
        {step === 2 && (
          <div className={`flex gap-6 justify-center items-stretch transition-all duration-300 mx-auto ${(policyMode === 'manual' || policyExtractId || (policyFields && Object.keys(policyFields).length > 0)) ? 'w-full px-4' : 'max-w-6xl'}`}>
          {/* Left info cards - only when panel not expanded */}
          {!(policyMode === 'manual' || policyExtractId || (policyFields && Object.keys(policyFields).length > 0)) && (
            <div className="hidden lg:flex flex-col w-72 flex-shrink-0 pt-2">
              <h3 className="text-sm font-bold text-gray-800 mb-1">What you'll unlock</h3>
              <p className="text-[10px] text-gray-400 mb-3">Upload your policy PDF to enable these on your dashboard</p>
              <div className="grid grid-cols-1 gap-2.5">
                {[
                  { title: 'Policy Benefits', desc: 'Auto-extract SI, maternity limits, room rent, co-pay, and 20+ coverage details from your policy.', icon: '🔍' },
                  { title: 'Industry Benchmarking', desc: 'See how your benefits compare against Top 25% and Top 10% employers in India.', icon: '📊' },
                  { title: 'AI Recommendations', desc: 'Get data-driven suggestions on which benefits to improve at renewal.', icon: '✨' },
                  { title: 'Maternity Limit Check', desc: 'Identify claims exceeding your policy maternity sub-limits - Normal & C-section.', icon: '👶' },
                  { title: 'Quick & Accurate', desc: 'AI reads your PDF and extracts all fields in 30–60 seconds. Text-searchable PDFs only.', icon: '🤖' },
                  { title: 'Optional Step', desc: 'You can skip this - claims analysis works without it. But benchmarking won\'t be available.', icon: '💡' },
                ].map((item, i) => (
                  <div key={i} className="bg-white/70 border border-gray-100 rounded-xl px-3 py-2.5 flex gap-2.5">
                    <span className="text-sm flex-shrink-0">{item.icon}</span>
                    <div>
                      <p className="text-[11px] font-semibold text-gray-700">{item.title}</p>
                      <p className="text-[10px] text-gray-400 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-pink-500" />
                <span className="text-xs text-gray-500">Upload complete &bull; Policy Copy</span>
              </div>
            </div>

            {error && <p className="text-xs text-red-500 mb-3 mt-2">{error}</p>}

            {/* ---- Policy Copy page ---- */}
            {(true) && (
              <>
                <h2 className="text-lg font-bold text-gray-900 mt-3 mb-1">Policy Copy <span className="text-sm font-normal text-gray-400">(Optional)</span></h2>
                <p className="text-xs text-gray-500 mb-1">Upload your policy PDF to auto-fill coverage details and enable benchmarking</p>
                <p className="text-[10px] text-gray-400 mb-3">AI extracts sum insured, maternity limits, room rent, co-pay and other benefits - used for policy benchmarking against industry standards.</p>

                <details className="mb-3 group">
                  <summary className="text-[11px] text-purple-600 font-medium cursor-pointer hover:text-purple-800">Why upload a policy copy?</summary>
                  <div className="mt-2 bg-purple-50 border border-purple-100 rounded-lg p-2.5 text-[11px] text-gray-600 space-y-1">
                    <p><strong className="text-gray-800">Benchmarking</strong> - compare SI, maternity, room rent against industry standards</p>
                    <p><strong className="text-gray-800">Recommendations</strong> - get data-driven suggestions to improve your policy</p>
                  </div>
                </details>

                {/* Section 1: Policy Copy - inline upload */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center text-[10px] font-bold text-purple-600">1</div>
                    <p className="text-xs font-semibold text-gray-700 flex items-center gap-1">Policy Copy <span className="relative group"><span className="w-3.5 h-3.5 rounded-full bg-gray-100 text-gray-400 hover:bg-purple-100 hover:text-purple-500 flex items-center justify-center text-[8px] font-bold cursor-help">i</span><span className="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-2 py-1.5 text-[9px] text-white bg-gray-800 rounded-lg shadow-lg w-56 text-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">Your insurance policy schedule/document (PDF). Used to extract Sum Insured, Maternity limits, Room Rent, Co-Pay and other benefits for benchmarking.</span></span></p>
                    {policyExtractId && <CheckCircle2 size={14} className="text-green-500 ml-1" />}
                    <span className="text-[10px] text-gray-400 ml-auto">Optional</span>
                  </div>
                  <div
                    className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all ${policyError ? 'border-red-300 bg-red-50/30 hover:border-red-400' : 'border-gray-200 hover:border-purple-400 hover:bg-purple-50/30'}`}
                    onClick={() => {
                      if (policyExtractId || policyError) {
                        // Reset everything so user can re-upload
                        setPolicyExtractId(null); setPolicyFile(null); setPolicyFields(null); setExtractionDone(false); setPolicyError('');
                        const inp = document.getElementById('policy-pdf-input2');
                        if (inp) inp.value = '';
                      }
                      document.getElementById('policy-pdf-input2').click();
                    }}
                    onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-purple-400', 'bg-purple-50/30'); }}
                    onDragLeave={e => { e.currentTarget.classList.remove('border-purple-400', 'bg-purple-50/30'); }}
                    onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-purple-400', 'bg-purple-50/30'); const f = e.dataTransfer.files[0]; if (f && f.name.toLowerCase().endsWith('.pdf')) { setPolicyFile(f); setPolicyMode('upload'); setPolicyError(''); setPolicyExtractId(null); setPolicyFields(null); setExtractionDone(false); } else if (f) { setError('Please upload a PDF file for policy copy.'); } }}
                  >
                    {policyFile ? (
                      <div className="flex items-center justify-center gap-2">
                        <FileText size={16} className={policyError ? 'text-red-400' : 'text-purple-500'} />
                        <span className={`text-sm font-medium ${policyError ? 'text-red-600' : 'text-gray-700'}`}>{policyFile.name}</span>
                        <button onClick={e => { e.stopPropagation(); setPolicyFile(null); setPolicyExtractId(null); setPolicyFields(null); setPolicyMode('choose'); setExtractionDone(false); setPolicyError(''); const inp = document.getElementById('policy-pdf-input2'); if (inp) inp.value = ''; }} className="text-xs text-gray-400 hover:text-red-500 ml-1">✕</button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <Upload size={16} className="text-purple-400" />
                        <p className="text-xs text-gray-500">Upload or drag & drop Policy PDF (max 10MB)</p>
                      </div>
                    )}
                    {policyError && (
                      <p className="text-[11px] text-red-500 mt-1.5">{policyError}</p>
                    )}
                    {(policyExtractId || policyError) && policyFile && (
                      <p className="text-[10px] text-purple-600 mt-1 underline">Click to re-upload a different file</p>
                    )}
                    <input id="policy-pdf-input2" type="file" accept=".pdf" className="hidden" onChange={e => { if (e.target.files[0]) { setPolicyFile(e.target.files[0]); setPolicyMode('upload'); setPolicyError(''); setPolicyExtractId(null); setPolicyFields(null); setExtractionDone(false); } }} />
                  </div>
                  {policyFile && !policyExtractId && !policyError && (
                    <button
                      onClick={handlePolicyUpload}
                      disabled={policyLoading}
                      className="w-full mt-2 py-2 text-xs font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {policyLoading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                      {policyLoading ? 'Uploading...' : 'Upload & Extract'}
                    </button>
                  )}
                  {policyMode !== 'manual' && !(policyFields && Object.keys(policyFields).length > 0) && !policyExtractId && (
                    <>
                      <div className="flex items-center gap-3 mt-3">
                        <div className="flex-1 h-px bg-gray-200" />
                        <span className="text-[10px] text-gray-400 font-medium">OR</span>
                        <div className="flex-1 h-px bg-gray-200" />
                      </div>
                      <button onClick={() => { setPolicyMode('manual'); loadPolicyFieldDefs(); if (!policyFields) setPolicyFields({}); }} className="w-full mt-2 py-2 text-xs font-semibold text-purple-600 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 flex items-center justify-center gap-2">
                        <FileText size={13} /> Enter Policy Details Manually
                      </button>
                    </>
                  )}
                </div>

                {/* Action buttons - extraction in progress or normal */}
                {policyExtractId && !policyFields && !policyError ? (
                  <div className="space-y-2">
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-2">
                      <div className="flex items-center gap-2 mb-2">
                        <Loader2 size={14} className="animate-spin text-purple-600" />
                        <span className="text-xs font-semibold text-purple-800">Extracting policy details - 30–60 sec</span>
                      </div>
                      <div className="w-full bg-purple-200 rounded-full h-1 mb-2">
                        <div className="bg-purple-600 h-1 rounded-full animate-pulse" style={{width: '60%'}} />
                      </div>
                    </div>
                    <button onClick={() => { syncPolicyFieldsToState(); setStep(3); }} className="w-full py-2.5 text-sm font-semibold text-purple-600 bg-purple-50 border-2 border-purple-200 rounded-lg hover:bg-purple-100 flex items-center justify-center gap-2">
                      <ArrowRight size={14} /> Continue to MIS - benefits will load on dashboard
                    </button>
                    <p className="text-[10px] text-gray-400 text-center">Or wait here - fields will auto-fill on the right when ready</p>
                  </div>
                ) : (
                  <>
                    {(policyFields && Object.keys(policyFields).length > 0) || policyMode === 'manual' ? (
                      <button onClick={() => { syncPolicyFieldsToState(); setStep(3); }} className="w-full py-2.5 text-sm font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2">
                        <ArrowRight size={14} /> Continue
                      </button>
                    ) : null}
                    <div className="flex justify-between mt-2">
                      <button onClick={() => setStep(1)} className="text-xs text-gray-400 hover:text-gray-600">&larr; Back</button>
                      <button onClick={() => { syncPolicyFieldsToState(); setStep(3); }} className="text-xs text-purple-500 hover:text-purple-700">Skip for now &rarr;</button>
                    </div>
                  </>
                )}
              </>
            )}

            {/* ---- PDF uploaded, extraction started - OLD VIEW (disabled, now inline) ---- */}
            {false && policyMode === 'upload' && policyExtractId && !policyFields && (
              <>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 mt-2">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle2 size={14} className="text-green-600" />
                    <span className="text-xs font-semibold text-green-700">Policy uploaded successfully!</span>
                  </div>
                  <p className="text-[10px] text-green-600">Extracting benefits from your policy document...</p>
                </div>

                <div className="flex items-center gap-3 mb-4 p-3 bg-purple-50 rounded-lg border border-purple-100">
                  <Loader2 size={16} className="animate-spin text-purple-500 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-purple-700">Auto-extracting policy benefits</p>
                    <p className="text-[10px] text-purple-500">This usually takes 30–60 seconds depending on PDF pages. You can wait to review or continue now.</p>
                  </div>
                </div>

                {/* MIS Summary - always visible */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center text-[10px] font-bold text-blue-600">2</div>
                    <p className="text-xs font-semibold text-gray-700 flex items-center gap-1">MIS Summary Report <span className="relative group"><span className="w-3.5 h-3.5 rounded-full bg-gray-100 text-gray-400 hover:bg-purple-100 hover:text-purple-500 flex items-center justify-center text-[8px] font-bold cursor-help">i</span><span className="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-2 py-1.5 text-[9px] text-white bg-gray-800 rounded-lg shadow-lg w-56 text-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">Management Information System report from your TPA/Insurer (PDF or Excel). Contains premium paid, policy period, and claims summary data.</span></span></p>
                    <span className="text-[10px] text-gray-400 ml-auto">Optional</span>
                  </div>
                  {misLoading ? (
                    <div className="flex items-center gap-3 p-3 border-2 border-blue-200 rounded-lg bg-blue-50">
                      <Loader2 size={16} className="animate-spin text-blue-500 flex-shrink-0" />
                      <p className="text-xs text-blue-600 font-medium">Extracting Premium & MIS Date...</p>
                    </div>
                  ) : (
                    <div className={`p-3 border-2 rounded-lg transition-colors ${misResult ? 'border-green-200 bg-green-50' : 'border-dashed border-gray-200 hover:border-blue-300'}`}
                      onDragOver={e => { e.preventDefault(); if (!misResult) e.currentTarget.classList.add('border-blue-400', 'bg-blue-50/30'); }}
                      onDragLeave={e => { e.currentTarget.classList.remove('border-blue-400', 'bg-blue-50/30'); }}
                      onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-blue-400', 'bg-blue-50/30'); if (misResult) return; const f = e.dataTransfer.files[0]; if (f) { const ext = f.name.split('.').pop().toLowerCase(); if (!['pdf','xlsx','xls','xlsb','xlsm','csv'].includes(ext)) { setError('Unsupported format for MIS.'); return; } setError(''); setMisLoading(true); setMisResult(null); setMisFile(f); uploadMisViaSignedUrl(f).then(res => { if (res.error) { setError(res.error); setMisLoading(false); return; } if (res.sheets?.length > 1) setMisSheets(res.sheets); if (res.premium) setPremium(String(Math.round(res.premium))); if (res.premiums?.inception_premium) setPremiumInception(String(Math.round(res.premiums.inception_premium))); if (res.misDate) setMisDate(res.misDate); if (res.policyStartDate) setPolicyStart(res.policyStartDate); if (res.premium || res.misDate) setMisResult({ premium: res.premium, premiums: res.premiums, misDate: res.misDate, misFileKey: res.misFileKey }); else setMisResult({ premium: null, premiums: {}, misDate: null, misFileKey: res.misFileKey || '' }); setMisLoading(false); }).catch(err => { setError('Failed: ' + err.message); setMisLoading(false); }); } }}
                    >
                      {misResult && (
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle2 size={12} className="text-green-600" />
                          <span className="text-[10px] font-semibold text-green-700">Auto-filled from MIS</span>
                          <button onClick={() => { setMisResult(null); setMisFile(null); setMisSheets([]); setPremium(''); }} className="text-[10px] text-gray-400 hover:text-red-500 ml-auto">Change file</button>
                        </div>
                      )}
                      <div className="flex gap-2 items-end">
                        <div className="flex-1">
                          <label className="text-[9px] text-gray-400 flex items-center gap-1">Premium (Till Date) Exc. GST <span className="relative group"><span className="w-3 h-3 rounded-full bg-gray-100 text-gray-400 hover:bg-purple-100 hover:text-purple-500 flex items-center justify-center text-[7px] font-bold cursor-help inline-flex">i</span><span className="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-2 py-1 text-[9px] text-white bg-gray-800 rounded shadow-lg w-48 text-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">Total premium paid till date including all endorsements (excluding GST)</span></span></label>
                          <input type="text" value={premium ? `₹${Number(premium).toLocaleString('en-IN')}` : ''} onChange={e => { const val = e.target.value.replace(/[₹,\s]/g, ''); setPremium(val || ''); if (misResult) setMisResult(prev => ({ ...prev, premium: val ? parseFloat(val) : null })); }} className={`w-full px-2 py-1.5 text-[11px] border rounded focus:border-purple-400 focus:outline-none ${misResult ? 'border-green-200 bg-white' : 'border-gray-200'}`} placeholder="e.g. ₹50,00,000" />
                        </div>
                        <div className="flex-1">
                          <label className="text-[9px] text-gray-400 flex items-center gap-1">MIS Date <span className="relative group"><span className="w-3 h-3 rounded-full bg-gray-100 text-gray-400 hover:bg-purple-100 hover:text-purple-500 flex items-center justify-center text-[7px] font-bold cursor-help inline-flex">i</span><span className="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-2 py-1 text-[9px] text-white bg-gray-800 rounded shadow-lg w-48 text-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">Date till which claims data is available in the MIS report</span></span></label>
                          <input type="date" min="2000-01-01" value={misResult?.misDate || misDate || ''} max={new Date().toISOString().split('T')[0]} onChange={e => { setMisDate(e.target.value); if (misResult) setMisResult(prev => ({ ...prev, misDate: e.target.value })); }} className={`w-full px-2 py-1.5 text-[11px] border rounded focus:border-purple-400 focus:outline-none ${misResult ? 'border-green-200 bg-white' : 'border-gray-200'}`} />
                        </div>
                        {!misResult && (
                          <label className="flex-shrink-0 cursor-pointer">
                            <span className="text-[10px] text-blue-500 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded hover:bg-blue-100 flex items-center gap-1"><FileSpreadsheet size={12} /> Upload MIS</span>
                            <input type="file" accept=".pdf,.xlsx,.xls,.xlsb,.xlsm,.csv" className="hidden" onChange={async e => {
                              const f = e.target.files[0]; if (!f) return;
                              setError(''); setMisLoading(true); setMisResult(null); setMisFile(f);
                              try {
                                const res = await uploadMisViaSignedUrl(f);
                                if (res.error) { setError(res.error); setMisLoading(false); return; }
                                if (res.sheets?.length > 1) setMisSheets(res.sheets);
                                if (res.premium) setPremium(String(Math.round(res.premium)));
                                if (res.premiums?.inception_premium) setPremiumInception(String(Math.round(res.premiums.inception_premium)));
                                if (res.misDate) setMisDate(res.misDate);
                                if (res.policyStartDate) setPolicyStart(res.policyStartDate);
                                if (res.premium || res.misDate) setMisResult({ premium: res.premium, premiums: res.premiums, misDate: res.misDate, misFileKey: res.misFileKey });
                                else { setMisResult({ premium: null, premiums: {}, misDate: null, misFileKey: res.misFileKey || '' }); }
                              } catch (err) { setError('Failed to extract: ' + err.message); }
                              setMisLoading(false); e.target.value = '';
                            }} />
                          </label>
                        )}
                      </div>
                      {/* Sheet picker - if Excel had multiple sheets */}
                      {misSheets.length > 1 && misFile && (
                        <div className="mt-2 pt-2 border-t border-green-200">
                          <p className="text-[10px] text-gray-500 mb-1">Wrong values? Try a different sheet:</p>
                          <div className="flex flex-wrap gap-1">
                            {misSheets.map(s => (
                              <button key={s} onClick={async () => {
                                setMisLoading(true); setError(''); setMisResult(null);
                                try {
                                  const res = await uploadMisViaSignedUrl(misFile, s);
                                  if (res.error) { setError(res.error); setMisLoading(false); return; }
                                  if (res.premium) setPremium(String(Math.round(res.premium)));
                                  if (res.premiums?.inception_premium) setPremiumInception(String(Math.round(res.premiums.inception_premium)));
                                  if (res.misDate) setMisDate(res.misDate);
                                  if (res.policyStartDate) setPolicyStart(res.policyStartDate);
                                  if (res.premium || res.misDate) setMisResult({ premium: res.premium, premiums: res.premiums, misDate: res.misDate, misFileKey: res.misFileKey });
                                  else setError(`No Premium or MIS Date found in sheet "${s}".`);
                                } catch (err) { setError('Failed: ' + err.message); }
                                setMisLoading(false);
                              }} className="px-2 py-0.5 text-[10px] bg-white border border-green-300 rounded hover:bg-green-100 transition-colors">
                                {s}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setStep(3)}
                  className="w-full py-2.5 text-sm font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2 mb-3"
                >
                  <ArrowRight size={14} /> Continue to next step
                </button>
                <p className="text-[10px] text-gray-400 text-center mb-2">Benefits will appear on the dashboard once extraction completes</p>
                <button onClick={() => { setPolicyExtractId(null); setPolicyFile(null); setPolicyMode('choose'); }} className="w-full text-xs text-gray-400 hover:text-gray-600">← Back</button>
              </>
            )}

            {/* ---- Extracted data banner removed - right panel shows data directly ---- */}
            {false && policyMode === 'upload' && policyFields && (
              <>

                {/* MIS Summary - always visible */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center text-[10px] font-bold text-blue-600">2</div>
                    <p className="text-xs font-semibold text-gray-700 flex items-center gap-1">MIS Summary Report <span className="relative group"><span className="w-3.5 h-3.5 rounded-full bg-gray-100 text-gray-400 hover:bg-purple-100 hover:text-purple-500 flex items-center justify-center text-[8px] font-bold cursor-help">i</span><span className="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-2 py-1.5 text-[9px] text-white bg-gray-800 rounded-lg shadow-lg w-56 text-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">Management Information System report from your TPA/Insurer (PDF or Excel). Contains premium paid, policy period, and claims summary data.</span></span></p>
                    <span className="text-[10px] text-gray-400 ml-auto">Optional</span>
                  </div>
                  {misLoading ? (
                    <div className="flex items-center gap-3 p-3 border-2 border-blue-200 rounded-lg bg-blue-50">
                      <Loader2 size={16} className="animate-spin text-blue-500 flex-shrink-0" />
                      <p className="text-xs text-blue-600 font-medium">Extracting Premium & MIS Date...</p>
                    </div>
                  ) : (
                    <div className={`p-3 border-2 rounded-lg transition-colors ${misResult ? 'border-green-200 bg-green-50' : 'border-dashed border-gray-200 hover:border-blue-300'}`}
                      onDragOver={e => { e.preventDefault(); if (!misResult) e.currentTarget.classList.add('border-blue-400', 'bg-blue-50/30'); }}
                      onDragLeave={e => { e.currentTarget.classList.remove('border-blue-400', 'bg-blue-50/30'); }}
                      onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-blue-400', 'bg-blue-50/30'); if (misResult) return; const f = e.dataTransfer.files[0]; if (f) { const ext = f.name.split('.').pop().toLowerCase(); if (!['pdf','xlsx','xls','xlsb','xlsm','csv'].includes(ext)) { setError('Unsupported format for MIS.'); return; } setError(''); setMisLoading(true); setMisResult(null); setMisFile(f); uploadMisViaSignedUrl(f).then(res => { if (res.error) { setError(res.error); setMisLoading(false); return; } if (res.sheets?.length > 1) setMisSheets(res.sheets); if (res.premium) setPremium(String(Math.round(res.premium))); if (res.premiums?.inception_premium) setPremiumInception(String(Math.round(res.premiums.inception_premium))); if (res.misDate) setMisDate(res.misDate); if (res.policyStartDate) setPolicyStart(res.policyStartDate); if (res.premium || res.misDate) setMisResult({ premium: res.premium, premiums: res.premiums, misDate: res.misDate, misFileKey: res.misFileKey }); else setMisResult({ premium: null, premiums: {}, misDate: null, misFileKey: res.misFileKey || '' }); setMisLoading(false); }).catch(err => { setError('Failed: ' + err.message); setMisLoading(false); }); } }}
                    >
                      {misResult && (
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle2 size={12} className="text-green-600" />
                          <span className="text-[10px] font-semibold text-green-700">Auto-filled from MIS</span>
                          <button onClick={() => { setMisResult(null); setMisFile(null); setMisSheets([]); setPremium(''); }} className="text-[10px] text-gray-400 hover:text-red-500 ml-auto">Change file</button>
                        </div>
                      )}
                      <div className="flex gap-2 items-end">
                        <div className="flex-1">
                          <label className="text-[9px] text-gray-400 flex items-center gap-1">Premium (Till Date) Exc. GST <span className="relative group"><span className="w-3 h-3 rounded-full bg-gray-100 text-gray-400 hover:bg-purple-100 hover:text-purple-500 flex items-center justify-center text-[7px] font-bold cursor-help inline-flex">i</span><span className="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-2 py-1 text-[9px] text-white bg-gray-800 rounded shadow-lg w-48 text-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">Total premium paid till date including all endorsements (excluding GST)</span></span></label>
                          <input type="text" value={premium ? `₹${Number(premium).toLocaleString('en-IN')}` : ''} onChange={e => { const val = e.target.value.replace(/[₹,\s]/g, ''); setPremium(val || ''); if (misResult) setMisResult(prev => ({ ...prev, premium: val ? parseFloat(val) : null })); }} className={`w-full px-2 py-1.5 text-[11px] border rounded focus:border-purple-400 focus:outline-none ${misResult ? 'border-green-200 bg-white' : 'border-gray-200'}`} placeholder="e.g. ₹50,00,000" />
                        </div>
                        <div className="flex-1">
                          <label className="text-[9px] text-gray-400 flex items-center gap-1">MIS Date <span className="relative group"><span className="w-3 h-3 rounded-full bg-gray-100 text-gray-400 hover:bg-purple-100 hover:text-purple-500 flex items-center justify-center text-[7px] font-bold cursor-help inline-flex">i</span><span className="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-2 py-1 text-[9px] text-white bg-gray-800 rounded shadow-lg w-48 text-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">Date till which claims data is available in the MIS report</span></span></label>
                          <input type="date" min="2000-01-01" value={misResult?.misDate || misDate || ''} max={new Date().toISOString().split('T')[0]} onChange={e => { setMisDate(e.target.value); if (misResult) setMisResult(prev => ({ ...prev, misDate: e.target.value })); }} className={`w-full px-2 py-1.5 text-[11px] border rounded focus:border-purple-400 focus:outline-none ${misResult ? 'border-green-200 bg-white' : 'border-gray-200'}`} />
                        </div>
                        {!misResult && (
                          <label className="flex-shrink-0 cursor-pointer">
                            <span className="text-[10px] text-blue-500 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded hover:bg-blue-100 flex items-center gap-1"><FileSpreadsheet size={12} /> Upload MIS</span>
                            <input type="file" accept=".pdf,.xlsx,.xls,.xlsb,.xlsm,.csv" className="hidden" onChange={async e => {
                              const f = e.target.files[0]; if (!f) return;
                              setError(''); setMisLoading(true); setMisResult(null); setMisFile(f);
                              try {
                                const res = await uploadMisViaSignedUrl(f);
                                if (res.error) { setError(res.error); setMisLoading(false); return; }
                                if (res.sheets?.length > 1) setMisSheets(res.sheets);
                                if (res.premium) setPremium(String(Math.round(res.premium)));
                                if (res.premiums?.inception_premium) setPremiumInception(String(Math.round(res.premiums.inception_premium)));
                                if (res.misDate) setMisDate(res.misDate);
                                if (res.policyStartDate) setPolicyStart(res.policyStartDate);
                                if (res.premium || res.misDate) setMisResult({ premium: res.premium, premiums: res.premiums, misDate: res.misDate, misFileKey: res.misFileKey });
                                else { setMisResult({ premium: null, premiums: {}, misDate: null, misFileKey: res.misFileKey || '' }); }
                              } catch (err) { setError('Failed to extract: ' + err.message); }
                              setMisLoading(false); e.target.value = '';
                            }} />
                          </label>
                        )}
                      </div>
                      {/* Sheet picker - if Excel had multiple sheets */}
                      {misSheets.length > 1 && misFile && (
                        <div className="mt-2 pt-2 border-t border-green-200">
                          <p className="text-[10px] text-gray-500 mb-1">Wrong values? Try a different sheet:</p>
                          <div className="flex flex-wrap gap-1">
                            {misSheets.map(s => (
                              <button key={s} onClick={async () => {
                                setMisLoading(true); setError(''); setMisResult(null);
                                try {
                                  const res = await uploadMisViaSignedUrl(misFile, s);
                                  if (res.error) { setError(res.error); setMisLoading(false); return; }
                                  if (res.premium) setPremium(String(Math.round(res.premium)));
                                  if (res.premiums?.inception_premium) setPremiumInception(String(Math.round(res.premiums.inception_premium)));
                                  if (res.misDate) setMisDate(res.misDate);
                                  if (res.policyStartDate) setPolicyStart(res.policyStartDate);
                                  if (res.premium || res.misDate) setMisResult({ premium: res.premium, premiums: res.premiums, misDate: res.misDate, misFileKey: res.misFileKey });
                                  else setError(`No Premium or MIS Date found in sheet "${s}".`);
                                } catch (err) { setError('Failed: ' + err.message); }
                                setMisLoading(false);
                              }} className="px-2 py-0.5 text-[10px] bg-white border border-green-300 rounded hover:bg-green-100 transition-colors">
                                {s}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <button onClick={() => { syncPolicyFieldsToState(); setStep(3); }} className="w-full py-2.5 text-sm font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2 mb-3">
                  <ArrowRight size={14} /> Looks good, Continue
                </button>
                <button onClick={() => { setPolicyFields(null); setPolicyFile(null); setPolicyExtractId(null); setExtractionDone(false); setPolicyMode('choose'); }} className="w-full text-xs text-gray-400 hover:text-gray-600">← Re-upload</button>
              </>
            )}

          </div>

          {/* ---- Right side panel for policy benefits (shows on manual click or after extraction) ---- */}
          {(policyMode === 'manual' || policyExtractId || (policyFields && Object.keys(policyFields).length > 0)) && (
          <div className={`bg-white rounded-2xl border border-gray-200 shadow-xl p-6 flex-1`}>
            {/* Header - slim extraction banner or normal title */}
            {policyExtractId && !policyFields && !policyError ? (
              <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg">
                <Loader2 size={14} className="animate-spin text-purple-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-[11px] font-semibold text-purple-800">Extracting...</span>
                  <span className="text-[10px] text-purple-500 ml-1">Fields will auto-fill below</span>
                </div>
                <div className="w-16 bg-purple-200 rounded-full h-1">
                  <div className="bg-purple-600 h-1 rounded-full animate-pulse" style={{width: '60%'}} />
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Policy Benefits</h3>
                  {extractionDone && policyFields && Object.keys(policyFields).length > 0 ? (
                    <p className="text-[10px] text-green-600 mt-0.5 flex items-center gap-1">
                      <CheckCircle2 size={10} className="text-green-500" />
                      Extraction complete - all fields are editable. Click any field to review & correct.
                    </p>
                  ) : (
                    <p className="text-[10px] text-gray-400 mt-0.5">Auto-filled from PDF or enter manually</p>
                  )}
                </div>
                <button onClick={() => { setPolicyMode('choose'); setPolicyFields(null); setPolicyExtractId(null); setExtractionDone(false); }} className="text-gray-400 hover:text-gray-600 text-lg font-bold">✕</button>
              </div>
            )}

            {/* Skeleton loader while extracting */}
            {policyExtractId && !policyFields && !policyError && (
              <div className="space-y-3" style={{animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'}}>
                <div className="grid grid-cols-3 gap-3">
                  {['Sum Insured', 'Family Structure', 'Co-Pay'].map(l => (
                    <div key={l}><span className="block text-[10px] font-semibold text-gray-400 mb-1">{l}</span><div className="h-8 rounded-lg bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 bg-[length:200%_100%] animate-[shimmer_1.5s_ease-in-out_infinite]" /></div>
                  ))}
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-purple-300 mb-1.5">Maternity Limits</p>
                  <div className="grid grid-cols-3 gap-3">
                    {['Normal (Metro)', 'C-Sec (Metro)', 'Pre-Post Natal', 'Normal (Non-Metro)', 'C-Sec (Non-Metro)', 'Infertility'].map(l => (
                      <div key={l}><span className="block text-[9px] text-gray-400 mb-0.5">{l}</span><div className="h-8 rounded-lg bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 bg-[length:200%_100%] animate-[shimmer_1.5s_ease-in-out_infinite]" /></div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-purple-300 mb-1.5">Room Rent</p>
                  <div className="grid grid-cols-2 gap-3">
                    {['Normal Room', 'ICU'].map(l => (
                      <div key={l}><span className="block text-[9px] text-gray-400 mb-0.5">{l}</span><div className="h-8 rounded-lg bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 bg-[length:200%_100%] animate-[shimmer_1.5s_ease-in-out_infinite]" /></div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-purple-300 mb-1.5">Other Benefits</p>
                  <div className="grid grid-cols-3 gap-3">
                    {['Corporate Buffer', 'Ambulance', 'Modern Treatment', 'Ayurvedic', 'Domiciliary', 'Cataract'].map(l => (
                      <div key={l}><span className="block text-[9px] text-gray-400 mb-0.5">{l}</span><div className="h-8 rounded-lg bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 bg-[length:200%_100%] animate-[shimmer_1.5s_ease-in-out_infinite]" /></div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-purple-300 mb-1.5">Inception Info</p>
                  <div className="grid grid-cols-3 gap-3">
                    {['Premium', 'Total Lives', 'Employees'].map(l => (
                      <div key={l}><span className="block text-[9px] text-gray-400 mb-0.5">{l}</span><div className="h-8 rounded-lg bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 bg-[length:200%_100%] animate-[shimmer_1.5s_ease-in-out_infinite]" /></div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Actual fields - shown after extraction or manual, or on error so user can enter manually */}
            {(!(policyExtractId && !policyFields) || policyError) && (() => {
              const ic = "w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:border-purple-400 focus:outline-none focus:ring-1 focus:ring-purple-200";
              const gv = (key) => (policyFields?.[key]?.formatted || (typeof policyFields?.[key] === 'string' ? policyFields[key] : '') || '');
              const sv = (key, label, section, val) => setPolicyFields(prev => ({...(prev || {}), [key]: {label, section, formatted: val, raw: val}}));
              // Standard answers per field (from /api/policy/fields): pick instead of typing; any text still works.
              const optionsByKey = {};
              Object.values(policyFieldDefs?.sections || {}).forEach(specs => (specs || []).forEach(spec => { if (spec.options) optionsByKey[spec.key] = spec.options; }));
              // A co-pay's wording is fixed and only the % changes — offer the conditions with the % already entered.
              const copayPct = (gv('copay').match(/\d+(?:\.\d+)?\s*%/) || [])[0];
              const suggestions = (key) => {
                const opts = optionsByKey[key] || [];
                return key === 'copay' && copayPct ? opts.map(o => o.replace(/\d+(?:\.\d+)?\s*%/, copayPct.replace(/\s+/g, ''))) : opts;
              };
              const listOf = (key) => (optionsByKey[key] ? `policy-options-${key}` : undefined);
              const fieldInfo = {
                sum_insured: 'Maximum amount the insurer will pay per person per year',
                structure: 'Who is covered - E (Employee), ES (+Spouse), ESC (+Children), ESCP (+Parents)',
                copay: 'Percentage of claim amount the employee must pay from their pocket',
                maternity_normal_metro: 'Coverage limit for normal delivery in metro cities',
                maternity_csec_metro: 'Coverage limit for C-section delivery in metro cities',
                pre_post_natal: 'Coverage for medical expenses before and after childbirth',
                maternity_normal_nonmetro: 'Coverage limit for normal delivery in non-metro cities',
                maternity_csec_nonmetro: 'Coverage limit for C-section delivery in non-metro cities',
                infertility: 'Coverage for infertility treatments like IVF, IUI',
                room_rent_normal: 'Daily room rent limit for standard hospital rooms',
                room_rent_icu: 'Daily room rent limit for ICU/critical care',
                corporate_buffer: 'Additional pool amount shared across all employees beyond individual SI',
                ambulance: 'Coverage for ambulance charges during emergency',
                modern_treatment: 'Coverage for advanced treatments like robotic surgery, stem cell therapy',
                ayurvedic_treatments: 'Coverage for AYUSH treatments (Ayurveda, Yoga, Unani, Siddha, Homeopathy)',
                domiciliary_treatment: 'Coverage for treatment taken at home when hospitalisation is not possible',
                cataract: 'Coverage limit for cataract surgery per eye',
                total_premium: 'Total premium paid at policy inception/start (excluding GST)',
                total_lives: 'Total number of lives (employees + dependents) covered at inception',
                employee_count: 'Total number of employees enrolled at policy inception',
                mis_premium: 'Total premium paid till date including all endorsements (excluding GST)',
                mis_date: 'The date till which the MIS report data is available',
              };
              const FL = ({ label, infoKey, big }) => (
                <label className={`flex items-center gap-1 ${big ? 'text-[10px] font-semibold text-gray-600 mb-1' : 'text-[9px] text-gray-500 mb-0.5'}`}>
                  {label}
                  {fieldInfo[infoKey] && (
                    <span className="relative group">
                      <Info size={10} className="text-gray-300 hover:text-purple-500 cursor-help" />
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-[9px] text-white bg-gray-800 rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">{fieldInfo[infoKey]}</span>
                    </span>
                  )}
                </label>
              );
              return (
                <div className="space-y-3">
                  {Object.keys(optionsByKey).map(key => (
                    <datalist key={key} id={`policy-options-${key}`}>
                      {[...new Set(suggestions(key))].map(o => <option key={o} value={o} />)}
                    </datalist>
                  ))}
                  {/* Row 1: Sum Insured + Family Structure + Co-Pay */}
                  <div className="grid grid-cols-3 gap-3">
                    <div><FL label="Sum Insured" infoKey="sum_insured" big /><input className={ic} placeholder="e.g. ₹5,00,000" value={gv('sum_insured')} onChange={e => sv('sum_insured', 'Sum Insured', 'Sum Insured', e.target.value)} /></div>
                    <div><FL label="Family Structure" infoKey="structure" big /><input className={ic} placeholder="e.g. ESC / ESCP" value={gv('structure')} onChange={e => sv('structure', 'Structure', 'Family Structure', e.target.value)} /></div>
                    <div><FL label="Co-Pay" infoKey="copay" big /><input className={ic} placeholder="e.g. No Co-pay" list={listOf('copay')} value={gv('copay')} onChange={e => sv('copay', 'Co-Pay', 'CoPay', e.target.value)} /></div>
                  </div>

                  {/* Maternity */}
                  <div>
                    <p className="text-[10px] font-semibold text-purple-700 mb-1.5">Maternity Limits</p>
                    <div className="grid grid-cols-3 gap-3">
                      <div><FL label="Normal (Metro)" infoKey="maternity_normal_metro" /><input className={ic} value={gv('maternity_normal_metro')} onChange={e => sv('maternity_normal_metro', 'Normal (Metro)', 'Maternity', e.target.value)} placeholder="₹50,000" /></div>
                      <div><FL label="C-Sec (Metro)" infoKey="maternity_csec_metro" /><input className={ic} value={gv('maternity_csec_metro')} onChange={e => sv('maternity_csec_metro', 'C-Sec (Metro)', 'Maternity', e.target.value)} placeholder="₹50,000" /></div>
                      <div><FL label="Pre-Post Natal" infoKey="pre_post_natal" /><input className={ic} list={listOf('pre_post_natal')} value={gv('pre_post_natal')} onChange={e => sv('pre_post_natal', 'Pre-Post Natal', 'Maternity', e.target.value)} placeholder="e.g. Covered" /></div>
                      <div><FL label="Normal (Non-Metro)" infoKey="maternity_normal_nonmetro" /><input className={ic} value={gv('maternity_normal_nonmetro')} onChange={e => sv('maternity_normal_nonmetro', 'Normal (Non-Metro)', 'Maternity', e.target.value)} placeholder="₹40,000" /></div>
                      <div><FL label="C-Sec (Non-Metro)" infoKey="maternity_csec_nonmetro" /><input className={ic} value={gv('maternity_csec_nonmetro')} onChange={e => sv('maternity_csec_nonmetro', 'C-Sec (Non-Metro)', 'Maternity', e.target.value)} placeholder="₹60,000" /></div>
                      <div><FL label="Infertility" infoKey="infertility" /><input className={ic} list={listOf('infertility')} value={gv('infertility')} onChange={e => sv('infertility', 'Infertility', 'Maternity', e.target.value)} placeholder="e.g. Covered" /></div>
                    </div>
                  </div>

                  {/* Room Rent */}
                  <div>
                    <p className="text-[10px] font-semibold text-purple-700 mb-1.5">Room Rent</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div><FL label="Normal Room" infoKey="room_rent_normal" /><input className={ic} value={gv('room_rent_normal')} onChange={e => sv('room_rent_normal', 'Normal Room', 'Room Rent', e.target.value)} placeholder="e.g. 2% of SI" /></div>
                      <div><FL label="ICU" infoKey="room_rent_icu" /><input className={ic} value={gv('room_rent_icu')} onChange={e => sv('room_rent_icu', 'ICU Limit', 'Room Rent', e.target.value)} placeholder="e.g. 4% of SI" /></div>
                    </div>
                  </div>

                  {/* Other Benefits */}
                  <div>
                    <p className="text-[10px] font-semibold text-purple-700 mb-1.5">Other Benefits</p>
                    <div className="grid grid-cols-3 gap-3">
                      <div><FL label="Corporate Buffer" infoKey="corporate_buffer" /><input className={ic} value={gv('corporate_buffer')} onChange={e => sv('corporate_buffer', 'Corporate Buffer', 'Other GHI', e.target.value)} placeholder="e.g. ₹10L" /></div>
                      <div><FL label="Ambulance" infoKey="ambulance" /><input className={ic} list={listOf('ambulance')} value={gv('ambulance')} onChange={e => sv('ambulance', 'Ambulance', 'Other GHI', e.target.value)} placeholder="e.g. ₹2,500" /></div>
                      <div><FL label="Modern Treatment" infoKey="modern_treatment" /><input className={ic} list={listOf('modern_treatment')} value={gv('modern_treatment')} onChange={e => sv('modern_treatment', 'Modern Treatment', 'Other GHI', e.target.value)} placeholder="e.g. 50% SI" /></div>
                      <div><FL label="Ayurvedic" infoKey="ayurvedic_treatments" /><input className={ic} list={listOf('ayurvedic_treatments')} value={gv('ayurvedic_treatments')} onChange={e => sv('ayurvedic_treatments', 'Ayurvedic', 'Other GHI', e.target.value)} placeholder="e.g. Covered" /></div>
                      <div><FL label="Domiciliary" infoKey="domiciliary_treatment" /><input className={ic} list={listOf('domiciliary_treatment')} value={gv('domiciliary_treatment')} onChange={e => sv('domiciliary_treatment', 'Domiciliary Treatment', 'Other GHI', e.target.value)} placeholder="e.g. Covered" /></div>
                      <div><FL label="Cataract" infoKey="cataract" /><input className={ic} list={listOf('cataract')} value={gv('cataract')} onChange={e => sv('cataract', 'Cataract', 'Disease Capping', e.target.value)} placeholder="e.g. Covered" /></div>
                    </div>
                  </div>

                  {/* Policy Info - Extracted Fields */}
                  <div>
                    <p className="text-[10px] font-semibold text-purple-700 mb-1.5">Inception Info</p>
                    <div className="grid grid-cols-3 gap-3">
                      <div><FL label="Premium (Inception)" infoKey="total_premium" /><input className={ic} value={gv('total_premium')} onChange={e => sv('total_premium', 'Premium', 'Policy Info', e.target.value)} placeholder="e.g. ₹1,85,00,000" /></div>
                      <div><FL label="Total Lives (Inception)" infoKey="total_lives" /><input className={ic} value={gv('total_lives')} onChange={e => sv('total_lives', 'Total Lives', 'Policy Info', e.target.value)} placeholder="e.g. 4285" /></div>
                      <div><FL label="Employees (Inception)" infoKey="employee_count" /><input className={ic} value={gv('employee_count')} onChange={e => sv('employee_count', 'Employees', 'Policy Info', e.target.value)} placeholder="e.g. 2096" /></div>
                    </div>
                  </div>

                </div>
              );
            })()}

          </div>
          )}


          </div>
        )}

        {/* ====== STEP 5: Verify & Generate ====== */}
        {step === 5 && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 max-w-md mx-auto">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 size={16} className="text-pink-500" />
              <span className="text-xs text-gray-500">Details reviewed &bull; Verify & Generate</span>
            </div>

            {otpStep === 'form' && (
              <>
                <h2 className="text-lg font-bold text-gray-900 mb-1 mt-4">Your Details</h2>
                <p className="text-xs text-gray-500 mb-1">Fill in your details to verify and generate the report</p>
                <p className="text-[10px] text-gray-400 mb-5">We verify via OTP to ensure data security. Your contact is used only for sharing the dashboard link.</p>
                {error && <p className="text-xs text-red-500 mb-3">{error}</p>}
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Your Name <span className="text-red-400">*</span></label>
                      <input value={visitorName} onChange={e => setVisitorName(e.target.value)} className={inputCls} placeholder="Full name" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Company Name <span className="text-red-400">*</span></label>
                      <input value={clientName} onChange={e => setClientName(e.target.value)} className={inputCls} placeholder="Your company name" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Phone Number <span className="text-red-400">*</span></label>
                      <div className="flex">
                        <span className="inline-flex items-center px-3 text-xs font-semibold text-gray-600 bg-gray-50 border border-r-0 border-gray-200 rounded-l-lg">+91</span>
                        <input type="tel" value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} onKeyDown={e => e.key === 'Enter' && handleRequestOTP()} className={inputCls + ' rounded-l-none'} placeholder="Enter 10-digit mobile number" maxLength={10} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Work Email <span className="text-red-400">*</span></label>
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleRequestOTP()} className={inputCls} placeholder="name@company.com" />
                    </div>
                  </div>

                  <button onClick={handleRequestOTP} disabled={otpLoading} className="w-full py-2.5 text-sm font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50">
                    {otpLoading ? 'Sending OTP...' : 'Send OTP'}
                  </button>
                </div>
                <div className="flex justify-between mt-2">
                  <button onClick={() => { setStep(4); setError(''); }} className="text-xs text-gray-400 hover:text-gray-600">&larr; Back</button>
                </div>
              </>
            )}

            {otpStep === 'verify' && (
              <>
                <h2 className="text-lg font-bold text-gray-900 mb-1 mt-4">Enter OTP</h2>
                <p className="text-xs text-gray-500 mb-5">OTP sent to <strong>{phone}</strong></p>
                {error && <p className="text-xs text-red-500 mb-3">{error}</p>}
                <input type="text" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} onKeyDown={e => e.key === 'Enter' && otp.length === 6 && handleVerifyOTP()} className={inputCls + ' text-center text-xl font-mono tracking-[0.3em] mb-3'} placeholder="------" maxLength={6} autoFocus />
                <button onClick={handleVerifyOTP} disabled={otpLoading || otp.length < 6} className="w-full py-2.5 text-sm font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50">
                  {otpLoading ? 'Verifying...' : 'Verify OTP'}
                </button>
                <div className="flex justify-between mt-2">
                  <button onClick={() => { setOtpStep('form'); setOtp(''); setError(''); }} className="text-xs text-gray-400 hover:text-gray-600">← Back</button>
                  <button onClick={handleRequestOTP} className="text-xs text-purple-600 hover:text-purple-800">Resend OTP</button>
                </div>
              </>
            )}

            {otpStep === 'verified' && (
              <div className="text-center py-4 mt-4">
                <CheckCircle2 size={32} className="text-pink-500 mx-auto mb-2" />
                <p className="text-sm font-semibold text-gray-800">Verified!</p>
                {statusMsg && <p className="text-xs text-purple-600 mt-2">{statusMsg}</p>}
                <div className="flex gap-3 justify-center mt-4">
                  <button onClick={handleMetadataSubmit} className="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-500 rounded-xl hover:from-purple-700 hover:to-pink-600 flex items-center justify-center gap-2 shadow-lg shadow-purple-200/50 transition-all">
                    <CheckCircle2 size={16} /> Generate Dashboard
                  </button>
                  <button onClick={() => window.location.href = '/'} className="px-5 py-2 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50">
                    Start Over
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ====== STEP 6: Generating ====== */}
        {step === 6 && (() => {
          const elapsed = statusMsg ? parseInt(statusMsg.match(/\d+/)?.[0] || '0') : 0;
          const steps = [
            { label: 'Reading & validating your claims file', threshold: 5 },
            { label: 'Auto-mapping columns to standard format', threshold: 15 },
            { label: 'Classifying claim statuses & types', threshold: 30 },
            { label: 'Analyzing diseases, hospitals & demographics', threshold: 50 },
            { label: 'Calculating ICR, TAT & utilization metrics', threshold: 70 },
            { label: 'Finalizing dashboard...', threshold: 90 },
          ];
          const tips = [
            { icon: '📊', title: 'ICR Analysis', desc: 'See your Incurred Claims Ratio and how it compares to industry benchmarks' },
            { icon: '🏥', title: 'Hospital Insights', desc: 'Top hospitals by claim amount, network vs non-network analysis' },
            { icon: '👨‍👩‍👧‍👦', title: 'Employee Patterns', desc: 'Identify top claimants and high SI utilization employees' },
            { icon: '🤰', title: 'Maternity Deep Dive', desc: 'Normal vs C-section split, metro vs non-metro, limit breaches' },
            { icon: '📈', title: 'AI Recommendations', desc: 'Data-driven suggestions for your next renewal negotiation' },
            { icon: '📋', title: 'PPT Export', desc: 'Download a presentation-ready report for your insurer meeting' },
          ];
          const tipIndex = Math.floor(elapsed / 10) % tips.length;
          return (
            <div className="max-w-lg mx-auto">
              <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-4">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Loader2 size={20} className="animate-spin text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Building Your Dashboard</h2>
                    <p className="text-[10px] text-gray-400">Usually takes 1–2 minutes</p>
                  </div>
                  {statusMsg && <span className="ml-auto text-xs text-purple-500 font-mono">{statusMsg.match(/\d+s/)?.[0] || ''}</span>}
                </div>

                {/* Animated checklist */}
                <div className="space-y-2 mb-5">
                  {steps.map((s, i) => {
                    const isDone = elapsed >= s.threshold;
                    const isActive = !isDone && (i === 0 || elapsed >= steps[i - 1].threshold);
                    return (
                      <div key={i} className={`flex items-center gap-2.5 py-1.5 px-3 rounded-lg transition-all duration-500 ${isDone ? 'bg-green-50' : isActive ? 'bg-purple-50' : ''}`}>
                        {isDone ? (
                          <CheckCircle2 size={14} className="text-green-500 flex-shrink-0" />
                        ) : isActive ? (
                          <Loader2 size={14} className="animate-spin text-purple-500 flex-shrink-0" />
                        ) : (
                          <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-200 flex-shrink-0" />
                        )}
                        <span className={`text-xs ${isDone ? 'text-green-700' : isActive ? 'text-purple-700 font-medium' : 'text-gray-400'}`}>{s.label}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Progress bar */}
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 h-1.5 rounded-full transition-all duration-1000" style={{ width: `${Math.min(elapsed * 1.1, 95)}%` }} />
                </div>
              </div>

              {/* Sneak peek carousel */}
              <div className="bg-white rounded-2xl border border-gray-200 p-4">
                <p className="text-[9px] text-gray-400 uppercase tracking-wider font-semibold mb-2">Coming up in your dashboard</p>
                <div className="flex items-start gap-3 transition-all duration-500">
                  <span className="text-2xl">{tips[tipIndex].icon}</span>
                  <div>
                    <p className="text-xs font-bold text-gray-800">{tips[tipIndex].title}</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">{tips[tipIndex].desc}</p>
                  </div>
                </div>
                <div className="flex justify-center gap-1 mt-3">
                  {tips.map((_, i) => <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === tipIndex ? 'bg-purple-500' : 'bg-gray-200'}`} />)}
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-4 flex items-start gap-2">
                  <AlertCircle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-600">{error}</p>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Policy Start Date popup */}
      {showMissingPopup && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle size={20} className="text-orange-500" />
              <h3 className="text-sm font-bold text-gray-900">Policy Start Date Required</h3>
            </div>
            <p className="text-xs text-gray-600 mb-4">
              Policy Start Date was not found in your raw data. Please enter it to generate accurate reports.
            </p>
            {error && <p className="text-xs text-red-500 mb-3">{error}</p>}
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Policy Start Date</label>
              <input type="date" min="2000-01-01" value={policyStart} onChange={e => {
                const s = e.target.value;
                setPolicyStart(s);
                setError('');
                if (s) { const d = new Date(s); d.setFullYear(d.getFullYear() + 1); d.setDate(d.getDate() - 1); setPolicyEnd(d.toISOString().split('T')[0]); }
              }} className={inputCls} />
            </div>
            <button onClick={handlePopupContinue} className="w-full py-2.5 text-sm font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700">
              Submit & Generate
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreprocessPage;
